
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketingTags from '@/components/MarketingTags';
import { CHECKOUT_TRUST_LINE, CheckoutTalkToTeam } from '@/components/booking/CheckoutPaymentTrustFooter';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionBand from '@/components/ui/SectionBand';
import AllInPricePill from '@/components/ui/AllInPricePill';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { cn } from '@/lib/utils';
import {
  REVIEW_SECTION_CARD,
  BRAND_NESTED_PANEL,
  BRAND_PRIMARY_BTN_CLASS,
} from '@/constants/brandColors';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ArrowLeft, CreditCard, Shield, CheckCircle, User, Loader2, X, Bed, Award, PlaneTakeoff, Star, ChevronDown, Info, Check, Tag } from 'lucide-react';
import CheckoutFaq from '@/components/checkout/CheckoutFaq';
import CheckoutReassuranceStrip from '@/components/checkout/CheckoutReassuranceStrip';
import CheckoutAcknowledgments from '@/components/checkout/CheckoutAcknowledgments';
import CheckoutTextAlertsOptIn from '@/components/checkout/CheckoutTextAlertsOptIn';
import ConciergeTripQuestions from '@/components/checkout/ConciergeTripQuestions';
import EmailMyPlan from '@/components/checkout/EmailMyPlan';
import { trackEvent } from '@/lib/activityTracker';
import { calculatePaymentSchedule as computePlanSchedule } from '@/utils/paymentScheduleCalculator';
import FlightOfferCard from '../components/search/FlightOfferCard';
import StayOfferCard from '../components/search/StayOfferCard';
import PassengerForm from '../components/passenger/PassengerForm';
import CheckoutTravelerReview from '@/components/checkout/CheckoutTravelerReview';
import SeatMap from '../components/seat/SeatMap';
import AuthGate from '../components/auth/AuthGate';
import TravelCreditsSection from '../components/booking/TravelCreditsSection';
import PaymentSchedule from '../components/booking/PaymentSchedule';
import PromoCodeField from '../components/booking/PromoCodeField';
import { createPageUrl, getServiceFeeRate, calculateServiceFee, getServiceFeeLabel, getServiceFeeDisplayLabel, formatDisplayPrice, dollarsToMiles, calculateReviewPagePricing, milesToDollars, isFullyRefundableOption } from '@/utils';
import { supabase } from '../lib/supabase';
import { reserveUrl, deferaCreditUrl } from '@/lib/apiUrls';
import { getReserveActAsUserId, mergeReserveActAsHeaders, mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { allocateDeferaCredit } from '@/utils/deferaCreditAllocator';
import {
  readCartDiscount,
  applyCheckoutDiscounts,
  buildBookingDiscountFields,
} from '@/utils/discountCodes';
import {
  checkoutMilesToUse,
  coversWholeTrip,
  maxCheckoutMiles,
  tripCentsForMiles,
} from '../../supabase/functions/_shared/miles-redemption.js';
import { CAPITAL_MIN_LEAD_DAYS } from '../../supabase/functions/_shared/capital-checkout-integrity.js';
import MilesCheckoutPanel from '@/components/checkout/MilesCheckoutPanel';
import MilesAppliedLine from '@/components/checkout/MilesAppliedLine';
import MilesOnlyCheckout from '@/components/checkout/MilesOnlyCheckout';
import { readStoredReferralCode, clearStoredReferralCode } from '@/hooks/useReferralCode';
import { validatePromoCode } from '@/api/functions';
import { scrollFinalStageIntoView, prefersReducedMotion } from './Review.finalStageScroll';
import { AbandonedTrip, SavedPassenger, UserPreferences } from '@/api/entities';
import { User as UserEntity } from '@/api/entities';
import { getCartStore } from '@/api/cartStore';
import { getOfferRequest } from '@/api/functions';
import { initiateSlicePayFlow } from '@/api/functions';
import { format, parseISO } from 'date-fns';
import { calculateUpfrontPayment } from '@/utils/installmentCalculator';
import { getDefaultSeatPreference, normalizeSeatPreference } from '@/utils/seatPreference';
import { getCityName } from '@/components/airportUtils';
import {
  calculatePaymentSchedule,
  DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_CAPITAL_HOLD_FEE_RATE,
  SLICE_PAY_HOLD_FEE_RATE,
  DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
} from '@/utils/paymentScheduleCalculator';
import { useBookingFlow } from '@/context/BookingFlowContext';
import { useHideNav } from '@/context/HideNavContext';
import { logError } from '@/lib/errorLogger';
// AddToReserveCard intentionally not imported — Defera Reserve was discontinued
// for new users, so we no longer offer the "add this trip to Reserve" entry
// point on the checkout review page.
import ReserveShortfallPaymentForm from '@/components/reserve/ReserveShortfallPaymentForm';
import DeferaZeroCheckoutForm from '@/components/defera-zero/DeferaZeroCheckoutForm';
import DeferaCapitalNmiCheckoutForm from '@/components/defera-capital-nmi/DeferaCapitalNmiCheckoutForm';
import { applePayAvailableForRail } from '@/constants/applePay';
import { buildFlightDataForApi } from './Review.flightDataForApi';
import { pricedTravelerList, travelerCountProblem, fitTravelersToPricedCount, travelerCountMessage } from '@/utils/pricedTravelers';
import { restoredCheckoutPlan, RESTORE_PICK_FARE_MESSAGE, RESTORE_SEARCH_MESSAGE } from '@/utils/cartCheckoutRestore';
import FayeInsuranceOffer from '@/components/checkout/FayeInsuranceOffer';
import { FAYE_INSURANCE_ENABLED } from '@/utils/fayeFlag';
import { buildFayeOffer, buildInsuranceSelection } from '../../supabase/functions/_shared/faye-checkout.js';
import { timezoneForAirport } from '../../server/airport-timezones.js';
import { buildSeatDataForFlightData } from '@/utils/seatDataForBooking';
import { buildTommyUrl, TOMMY_INTENTS } from '@/constants/support';
import { resolveMarketingFareNameFromOffer } from '@/utils/marketingFareName';
import {
  resolveCheckoutPaymentMethod,
  isDeferaZeroRefundableInstallmentsPath as isDeferaZeroPath,
} from '@/utils/checkoutPaymentMethod';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { useDeferaCapitalProcessor } from '@/hooks/useDeferaCapitalProcessor';
import useTripTravelRequirements from '@/hooks/useTripTravelRequirements';
import { missingTravelDocument } from '@/utils/travelRequirements';
import { fmtCurrency } from '@/utils/money';
// Helper functions to compare flight data, ensuring we can find the refreshed offer
const areFlightSegmentsEquivalent = (segmentA, segmentB) => {
  if (!segmentA || !segmentB) return false;
  return (
    segmentA.origin.iata_code === segmentB.origin.iata_code &&
    segmentA.destination.iata_code === segmentB.destination.iata_code &&
    segmentA.departing_at === segmentB.departing_at &&
    segmentA.arriving_at === segmentB.arriving_at &&
    segmentA.marketing_carrier.iata_code === segmentB.marketing_carrier.iata_code &&
    segmentA.marketing_carrier_flight_number === segmentB.marketing_carrier_flight_number);
};

const areSlicesEquivalent = (sliceA, sliceB) => {
  if (!sliceA || !sliceB || sliceA.segments.length !== sliceB.segments.length) {
    return false;
  }
  for (let i = 0; i < sliceA.segments.length; i++) {
    if (!areFlightSegmentsEquivalent(sliceA.segments[i], sliceB.segments[i])) {
      return false;
    }
  }
  return true;
};


// Helper function to validate return slice is distinct from outbound
// Detects corrupted data where return slice is an EXACT COPY of outbound (same segments/times)
// NOTE: Same flight numbers are VALID - some airlines reuse flight numbers for round trips (e.g., AA 6210 SAT→LAX and LAX→SAT)
const validateReturnSlice = (outboundSlice, returnSlice, bookingOption) => {
  if (!outboundSlice || !returnSlice) return { isValid: false, reason: 'missing_slice', isCritical: true };
  
  const outSegments = outboundSlice.segments;
  const retSegments = returnSlice.segments;
  
  if (!outSegments?.length || !retSegments?.length) return { isValid: false, reason: 'missing_segments', isCritical: true };
  
  // For connecting flights, use first segment's origin and LAST segment's destination
  const outFirstSeg = outSegments[0];
  const outLastSeg = outSegments[outSegments.length - 1];
  const retFirstSeg = retSegments[0];
  const retLastSeg = retSegments[retSegments.length - 1];
  
  // Check if routes are correctly reversed (return should be destination → origin)
  // Outbound: A → ... → B, Return: B → ... → A
  const outOrigin = outFirstSeg.origin?.iata_code;
  const outFinalDest = outLastSeg.destination?.iata_code;
  const retOrigin = retFirstSeg.origin?.iata_code;
  const retFinalDest = retLastSeg.destination?.iata_code;
  
  if (retOrigin !== outFinalDest || retFinalDest !== outOrigin) {
    console.error('❌ Invalid return route:', { outOrigin, outFinalDest, retOrigin, retFinalDest });
    return { isValid: false, reason: 'invalid_route', isCritical: true };
  }
  
  // CRITICAL: Check for TRUE corruption - when return slice is an EXACT COPY of outbound
  // This uses areSlicesEquivalent which compares ALL segment details (times, airports, carriers)
  // Same flight numbers with different times/dates is VALID (airlines reuse numbers)
  if (areSlicesEquivalent(outboundSlice, returnSlice)) {
    console.error('❌ Corrupted data: return slice is exact mirror of outbound (identical segments/times)');
    
    // Get correct return flight number from bookingOption for error message
    let correctFlightNumber = null;
    if (bookingOption?.returning?.marketed_as?.[0]) {
      correctFlightNumber = bookingOption.returning.marketed_as[0];
    } else if (bookingOption?.together?.marketed_as?.length > 1) {
      correctFlightNumber = bookingOption.together.marketed_as[1];
    }
    
    return { 
      isValid: false, 
      reason: 'mirrored_slice_data',
      correctFlightNumber,
      isCritical: true,
      message: `Return flight data is corrupted (exact copy of outbound). Please search again and re-select your flights.`
    };
  }
  
  // NOTE: Time anomaly check REMOVED
  // International flights crossing timezones (especially Tokyo -> North America) 
  // legitimately "arrive before they depart" in local times.
  // For example: Leave Tokyo 18:10 JST, arrive Toronto 17:35 EST same day
  // This is NOT corruption - it's timezone math.
  
  return { isValid: true };
};

/**
 * Loading state shown while `useDeferaCapitalProcessor()` resolves which payment
 * track to render. After 8 seconds we surface a recovery hint so a customer
 * caught behind a stuck `/api/defera-capital-processor` response isn't trapped
 * staring at a spinner — the hook has its own 8s timeout, so by the time the
 * recovery hint appears the hook has already given up and stays at `null`.
 */
function CapitalProcessorLoading() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <Loader2 className="w-5 h-5 animate-spin text-white/60" aria-hidden="true" />
      <div className="text-white/60 text-sm">Loading payment options…</div>
      {slow && (
        <div className="text-white/50 text-xs max-w-sm text-center mt-2">
          This is taking longer than usual. Please refresh the page. If the
          problem persists, contact us at defera.app/contact.
        </div>
      )}
    </div>
  );
}

// Cache buster: v2.1
export default function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    updateBookingState,
    alignSessionId,
    sessionId: contextSessionId,
    passengers: contextPassengers,
    searchParams: contextSearchParams,
    selectedFlight: contextFlight,
    returnFlight: contextReturn,
    bookingOption: contextBookingOption,
    paymentMethod: contextPayment,
    choseRefundableInstallments: contextChoseRefundableInstallments,
    isHydrated: bookingFlowHydrated,
  } = useBookingFlow();

  /** Resume from a recovery email: /review?resume=<cartId>&t=<token>. The
   *  server verifies the token and returns the cart's session id; aligning the
   *  booking flow to it rehydrates that cart, the same way a dashboard resume
   *  does. The cart itself rides along for a customer who opens the link
   *  signed out, whose browser can't read their own cart. While the round trip
   *  is in flight the mount effect below must not bounce an "empty" context
   *  back to search. */
  const resumeHandledRef = useRef(false);
  /** True from the first render of a resume link until its cart is loaded:
   *  nothing is priced, saved or bounced from the flow this browser held
   *  before (state, so the page re-checks once the cart lands). */
  const [resumeInFlight, setResumeInFlight] = useState(() => {
    const params = new URLSearchParams(location.search || '');
    return !!(params.get('resume') && params.get('t'));
  });
  useEffect(() => {
    if (resumeHandledRef.current) return;
    const params = new URLSearchParams(location.search || '');
    const cart = params.get('resume');
    const token = params.get('t');
    if (!cart || !token) return;
    resumeHandledRef.current = true;
    (async () => {
      let expiredSearchPath = null;
      try {
        const res = await fetch(`/api/checkout/resume?cart=${encodeURIComponent(cart)}&t=${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (res.ok && body?.session_id) {
          trackEvent('checkout_resumed_from_email', { cart });
          await alignSessionId(body.session_id, { cart: body.cart });
        } else if (res.status === 410 && typeof body?.search_path === 'string' && body.search_path.startsWith('/search')) {
          // The link expired (14 days): prices from then mean nothing now.
          trackEvent('checkout_resume_link_expired', { cart });
          expiredSearchPath = body.search_path;
        }
      } catch {
        // Fall through: the page behaves as a normal visit.
      } finally {
        try { window.history.replaceState({}, document.title, location.pathname); } catch { /* ignore */ }
        setResumeInFlight(false);
        if (expiredSearchPath) {
          toast.info('This link has expired. Search again to see today’s prices for your trip.');
          navigate(expiredSearchPath, { replace: true });
        }
      }
    })();
  }, [location.search, location.pathname, alignSessionId, navigate]);

  /** One "reached the review step" event per visit — the funnel's stall point. */
  const reviewViewedRef = useRef(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  // Which countries the flights touch: international trips require each
  // traveler's passport before payment opens.
  const travelRequirement = useTripTravelRequirements(selectedFlight);
  const [paymentLink, setPaymentLink] = useState(null);
  const [seatSelections, setSeatSelections] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [passengersPersistedToDb, setPassengersPersistedToDb] = useState(false); // CRITICAL: Track if passengers are saved to DB
  // A restored cart can carry travelers saved before passports were required.
  const travelDocsComplete =
    (!selectedFlight?.slices?.length || travelRequirement.ready) &&
    passengers.every((p) => missingTravelDocument(p, travelRequirement).length === 0);
  const [showPassengerForm, setShowPassengerForm] = useState(false);
  const [blacklistBlock, setBlacklistBlock] = useState(null); // { matched_types: string[] }
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [abandonedTripId, setAbandonedTripId] = useState(null);
  // Forward navigation's own fare, else the fare restored with the cart (never
  // a fare left in the booking flow by an earlier selection).
  const bookingOptionForReview = location.state?.selectedFlight
    ? (location.state.bookingOption ?? null)
    : contextBookingOption;
  const isRefundableFareForReview = !!(bookingOptionForReview && isFullyRefundableOption(bookingOptionForReview));
  const choseRefundableInstallments = location.state?.choseRefundableInstallments === true || contextChoseRefundableInstallments === true;
  // When user chose Installments variant (bypass DZ), seed with the Slice-style base deposit (20%) until
  // PaymentSchedule mounts and snaps to the lowest available tier for the actual checkout provider
  // (DC: 5% at 90+ days down to 50% close-in; Slice: 20–50%). Refundable Defera Zero stays at 0%.
  const [selectedDeposit, setSelectedDeposit] = useState(() => (isRefundableFareForReview && !choseRefundableInstallments ? 0 : 20));
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState('weekly'); // Track selected payment plan

  // Sync selectedDeposit to 0 when refundable fare is detected — but NOT when user chose Installments variant
  useEffect(() => {
    if (isRefundableFareForReview && !choseRefundableInstallments) {
      setSelectedDeposit(0);
    }
  }, [isRefundableFareForReview, choseRefundableInstallments]);
  const [isRefreshingOffer, setIsRefreshingOffer] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [allSegmentsCompleted, setAllSegmentsCompleted] = useState(false);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [showMilesConfirmationModal, setShowMilesConfirmationModal] = useState(false);
  const [hasOpenedPaymentLink, setHasOpenedPaymentLink] = useState(false);  // Disable button after opening
  const [appliedCredits, setAppliedCredits] = useState([]);  // Travel credits, certificates, gift cards
  const [creditsLoaded, setCreditsLoaded] = useState(false);  // Flag to gate SlicePay until credits are hydrated
  const [promoApplied, setPromoApplied] = useState(false);  // Promo code applied (0% service fee) - validated server-side only
  // Discount code (migration 141) — a flat sum off the grand total, INDEPENDENT
  // of promoApplied above. Shape: { code, codeId, cents, viaLink } or null.
  // Never set from user input: it is read back from the cart after the server
  // validated the code, so the amount is always server-authoritative.
  const [discount, setDiscount] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);  // Track authenticated user for credits section
  const [termsAccepted, setTermsAccepted] = useState(false);  // Cancellation & Refund policy + ToS/Privacy assent
  const [bookingAuthorizationAccepted, setBookingAuthorizationAccepted] = useState(false);  // "How this works" — Defera advances funds, ticket released on full payment
  const [releaseUponFullPaymentAccepted, setReleaseUponFullPaymentAccepted] = useState(false);  // Chargebacks & friendly fraud + data-collection authorization
  const [defaultAcknowledgmentAccepted, setDefaultAcknowledgmentAccepted] = useState(false);  // Default & collections — credit-bureau reporting consent
  const [ticketIssuanceDelayAccepted, setTicketIssuanceDelayAccepted] = useState(false);  // Ticket issuance may be delayed pending verification
  /** The review page shows ONE checkbox (CheckoutAcknowledgments) with the five
   *  acknowledgment texts one tap away; accepting it accepts all five. The five
   *  flags stay separate so the payment gate below is unchanged. */
  const setAllAcknowledgments = (next) => {
    setTermsAccepted(next);
    setBookingAuthorizationAccepted(next);
    setReleaseUponFullPaymentAccepted(next);
    setDefaultAcknowledgmentAccepted(next);
    setTicketIssuanceDelayAccepted(next);
  };
  /** Free Flight Upgrade — a REQUIRED choice, deliberately not an acknowledgment.
   *  The five checkboxes above are legal assent (all must be ticked); this is a
   *  preference where "No" is an equally valid answer, so it renders as a
   *  two-option pick and gates on "answered", never on "agreed". Kept visually
   *  and structurally separate so nobody reads a service preference as another
   *  thing they must consent to in order to buy.
   *    null = unanswered (blocks purchase) · true = opted in · false = declined */
  const [fasterFlightOptIn, setFasterFlightOptIn] = useState(null);
  /** Faye travel insurance: { tier, acceptedAt } once a plan is added,
   *  { declined: true } for "No thanks", null before they answer (optional). */
  const [insuranceChoice, setInsuranceChoice] = useState(null);
  /** Bumped when checkout refuses the travel insurance, so the offer is priced
   *  again on today's date (a long-open tab can cross a birthday). */
  const [fayeQuoteKey, setFayeQuoteKey] = useState(0);
  /** Checkout said travel insurance isn't available: stop offering it. */
  const [insuranceUnavailable, setInsuranceUnavailable] = useState(false);
  /** Only meaningful when opted in, and deliberately NOT defaulted. Pre-selecting
   *  "any airline" would record a scope the customer never actually chose — and a
   *  pre-filled option is one people skim past without registering they had a
   *  say. Both questions start from a clean slate. */
  const [fasterFlightAirlinePref, setFasterFlightAirlinePref] = useState(null);
  /** Answered = declined outright, OR opted in AND scoped. Opting in without
   *  picking a scope is a half-answer, so it keeps the purchase gate closed. */
  const fasterFlightAnswered = fasterFlightOptIn === false
    || (fasterFlightOptIn === true && fasterFlightAirlinePref !== null);
  /** Travel-purpose questions (2026-09-30). All required, and all deliberately
   *  un-defaulted for the same reason as the upgrade scope above: a pre-selected
   *  answer is one people skim past, and it records an intent nobody expressed.
   *    purpose: null | 'vacation' | 'business' | 'event'
   *    withOthers: null | true | false
   *    companion: null | 'defera' | 'elsewhere' — only asked on `true` */
  const [travelPurpose, setTravelPurpose] = useState(null);
  const [travelingWithOthers, setTravelingWithOthers] = useState(null);
  const [companionBooking, setCompanionBooking] = useState(null);
  /** Optional, so it never gates the purchase; only saved under "Another
   *  Defera booking" (see buildFlightDataForApi). */
  const [companionBookingReference, setCompanionBookingReference] = useState('');
  /** The final stage is revealed by the passenger modal closing, which inserts
   *  the whole block ABOVE the button that was just pressed and leaves the page
   *  scrolled into the middle of it — terms checkbox clipped off the top, nav
   *  pill over the rest. Counter rather than a boolean so editing passengers and
   *  resubmitting re-aims the viewport too; see Review.finalStageScroll.js. */
  const [finalStageReveals, setFinalStageReveals] = useState(0);
  const reviewAndPayRef = useRef(null);
  /** Saying "yes, with others" and stopping is a half-answer, exactly like
   *  opting into an upgrade without scoping it — the gate stays closed. */
  const travelQuestionsAnswered = travelPurpose !== null
    && (travelingWithOthers === false
      || (travelingWithOthers === true && companionBooking !== null));

  /** Every LEGAL assent ticked. Split out so the blocked-purchase message can
   *  name the one thing actually missing — "Must accept all acknowledgments"
   *  sends someone hunting through five checkboxes they already ticked when the
   *  real gap is an unanswered preference below them. */
  const allAcknowledgmentsAcceptedExceptUpgrade = termsAccepted && bookingAuthorizationAccepted && releaseUponFullPaymentAccepted && defaultAcknowledgmentAccepted && ticketIssuanceDelayAccepted;
  // Aggregate gate: every acknowledgment must be accepted before payment can proceed
  const allAcknowledgmentsAccepted = allAcknowledgmentsAcceptedExceptUpgrade
    && fasterFlightAnswered
    && travelQuestionsAnswered;
  /** Names the booked carrier in the "Only <airline>" scope option — "Only
   *  Delta" is a real choice a frequent flyer can weigh; "Only my airline"
   *  makes them guess which one we mean on a multi-carrier itinerary. Falls
   *  back to the generic label when the carrier can't be resolved. */
  const primaryAirlineName = useMemo(() => {
    const seg = selectedFlight?.slices?.[0]?.segments?.[0];
    const name = seg?.marketing_carrier?.name || seg?.airline?.name;
    return typeof name === 'string' && name.trim() && name !== 'Unknown' ? name.trim() : null;
  }, [selectedFlight]);
  const [termsOverlay, setTermsOverlay] = useState(null);  // 'terms' | 'privacy' | null for full-screen document overlay
  const [showTermsRequired, setShowTermsRequired] = useState(false);  // Show "Must accept terms" when clicking Book Now without accepting
  const termsRequiredTimerRef = useRef(null);
  /** What is ACTUALLY missing when a purchase is blocked, in page order:
   *  acknowledgments, then the upgrade pick, then the travel questions. Doubles
   *  as the analytics reason so the funnel stops filing an unanswered travel
   *  question as an upgrade refusal — the old expression only knew about two of
   *  the three gates and reported 'upgrade_choice' for both of the later ones. */
  const blockedPurchaseReason = !allAcknowledgmentsAcceptedExceptUpgrade
    ? 'terms'
    : !fasterFlightAnswered
      ? 'upgrade_choice'
      : !travelQuestionsAnswered
        ? 'travel_questions'
        : null;
  const blockedPurchaseMessage = {
    terms: 'Please accept the acknowledgments to continue',
    upgrade_choice: 'Choose a Free Flight Upgrade option to continue',
    travel_questions: 'Answer the travel questions below to continue',
  }[blockedPurchaseReason] ?? null;
  /** Every blocked purchase attempt goes through here. The message it raises
   *  renders at the top of the acknowledgment block while the pay button sits
   *  below every question — so by the time someone can press that button the
   *  message is usually off-screen above them, it clears itself after two
   *  seconds, and a blocked press reads as a dead button. Raise it and bring the
   *  block it belongs to back into view together. */
  const flagTermsRequired = useCallback(() => {
    if (blockedPurchaseReason) trackEvent('checkout_book_blocked', { reason: blockedPurchaseReason });
    setShowTermsRequired(true);
    // Repeated presses used to stack timers, so the second one could clear the
    // message ~0ms after the third press raised it.
    if (termsRequiredTimerRef.current) clearTimeout(termsRequiredTimerRef.current);
    termsRequiredTimerRef.current = setTimeout(() => setShowTermsRequired(false), 2000);
    scrollFinalStageIntoView(reviewAndPayRef.current, { reduceMotion: prefersReducedMotion() });
  }, [blockedPurchaseReason]);
  useEffect(() => () => {
    if (termsRequiredTimerRef.current) clearTimeout(termsRequiredTimerRef.current);
  }, []);
  const { setHideNav } = useHideNav();
  /** The travelers the fare was PRICED for (the search), never the travelers
   *  entered: this used to fall back to the saved travelers on a refresh or a
   *  cart resume, so 3 travelers saved from an earlier cart rode onto a one-seat
   *  fare (C4J47L). See src/utils/pricedTravelers.js. */
  const pricedTravelers = useMemo(() => pricedTravelerList({
    statePassengers: location.state?.passengers,
    contextSearchParams,
    selectedFlight: location.state?.selectedFlight || contextFlight,
  }), [location.state?.passengers, location.state?.selectedFlight, contextSearchParams, contextFlight]);
  const searchPassengers = useMemo(() => pricedTravelers || [], [pricedTravelers]);
  const pricedTravelerCount = pricedTravelers?.length || 0;
  /** How the companion question is worded. The "aside from passengers on this
   *  reservation" clause is nonsense on a solo booking. */
  const isMultiPassengerBooking = (searchPassengers.length || 1) > 1;
  /** Switching back to "No" must drop the follow-up, or a stale companion
   *  answer rides along to a booking whose owner said they travel alone. */
  useEffect(() => {
    if (travelingWithOthers !== true && companionBooking !== null) setCompanionBooking(null);
  }, [travelingWithOthers, companionBooking]);

  // Hide layout nav pill when Terms/Privacy overlay is open
  useEffect(() => {
    setHideNav(!!termsOverlay);
    return () => setHideNav(false);
  }, [termsOverlay, setHideNav]);
  const hasAutoInitiatedPayment = useRef(false);
  const trackInFlightRef = useRef(false);  // Guard against concurrent trackAbandonedTrip calls
  const lastTrackedSessionRef = useRef(null);  // Track last successfully tracked session to prevent redundant calls
  // trackAbandonedTrip is memoized once, so it reads the booking session here:
  // a recovery link aligns the session AFTER mount, and the cart has to be
  // saved under that session, the only key a signed-out guest can read it by.
  const contextSessionIdRef = useRef(contextSessionId);
  contextSessionIdRef.current = contextSessionId;
  const paymentLinkOpenedRef = useRef(false);  // Guard against multiple SlicePay link opens
  const fromUpload = location.state?.fromUpload || false;
  const uploadedImageUrl = location.state?.uploadedImageUrl || null;
  const selectedHotel = location.state?.selectedHotel || null;
  const packageType = location.state?.packageType || 'flight';
  // FlightOptions snapshots its own entry state (bookingToken, raw
  // paymentMethod, search context) into this object and threads it here via
  // SelectSeats. When present, we can send the customer back to Booking
  // Options to pick a different fare without losing the flow — FlightOptions
  // re-fetches its options from the bookingToken, so navigating back with
  // anything less bounces to /search with "Session expired". Absent on flows
  // that never had a Booking Options step (Duffel direct, upload, resume).
  const bookingOptionsReturn = location.state?.bookingOptionsReturn || null;
  // The old "Miles" and "Miles + Installments" payment methods are retired
  // (2026-10-05): they parked the cart for ops to finish by hand, and no
  // customer ever completed one. Miles are now applied on the normal
  // checkout (the Miles panel below), so a link or cart still carrying one
  // checks out as installments.
  const requestedPaymentMethod = location.state?.paymentMethod || contextPayment || 'installments';
  const paymentMethod = requestedPaymentMethod === 'miles' || requestedPaymentMethod === 'miles_installments'
    ? 'installments'
    : requestedPaymentMethod;
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const deferaCapitalProcessor = useDeferaCapitalProcessor();

  /** Refundable fare + installments + stay on Defera Zero ($0-down) — still uses `paymentMethod === 'installments'`; must not be classified as Capital when platform default is `defera_capital`. */
  const isDeferaZeroRefundableInstallmentsPath = isDeferaZeroPath({
    paymentMethod,
    isRefundableFare: isRefundableFareForReview,
    choseRefundableInstallments,
  });

  // The whole decision table (Zero upsell, Capital routing, the XFZXIZ
  // flight+hotel guard, and the provider-still-loading hold) lives in
  // src/utils/checkoutPaymentMethod.js so it can be tested. It was inline
  // here and therefore unverified, which is how it spent months overriding
  // customers who had explicitly asked for a deposit plan (Collista
  // Lewingdon, 2026-09-17 — billed $0-down four times on a refundable
  // Southwest fare). `choseRefundableInstallments` is now final in that
  // table, and it survives a rehydration because the cart carries
  // flight_data._chose_refundable_installments.
  //
  // `POST /api/admin/bookings` still has the mirroring server-side guard
  // (`server/coerce-defera-zero-on-flight-stay.js`) so a hand-crafted
  // request that skips this component can't persist a flight+hotel Zero row.
  const isFlightHotelPackage = packageType === 'flight_stay';
  const effectivePaymentMethod = useMemo(
    () => resolveCheckoutPaymentMethod({
      paymentMethod,
      isRefundableFare: isRefundableFareForReview,
      choseRefundableInstallments,
      installmentsCheckoutProvider,
      isFlightHotelPackage,
    }),
    [
      choseRefundableInstallments,
      paymentMethod,
      isRefundableFareForReview,
      installmentsCheckoutProvider,
      isFlightHotelPackage,
    ]
  );

  const installmentsProviderReady = installmentsCheckoutProvider !== null;
  const awaitingInstallmentsProvider =
    !installmentsProviderReady &&
    !choseRefundableInstallments &&
    paymentMethod === 'installments' &&
    isRefundableFareForReview;

  /** Only Defera Zero uses $0-down + Zero-specific schedule UI. Defera Capital mirrors Slice Pay. */
  const deferaZeroStyleSchedule = effectivePaymentMethod === 'defera_zero';
  const isDeferaCapitalCheckout = effectivePaymentMethod === 'defera_capital';
  /** True while the Apple Pay sheet is open, so the NMI card form below it freezes. */
  const [applePayActive, setApplePayActive] = useState(false);
  const showDeferaStripeCheckout =
    installmentsProviderReady && (deferaZeroStyleSchedule || isDeferaCapitalCheckout);
  /** Capital fee/deposit tiers for Slice→Stripe installment routing (not Defera Zero). */
  const deferaCapitalServiceFeeTier = useMemo(
    () =>
      installmentsCheckoutProvider === 'defera_capital' &&
      !isDeferaZeroRefundableInstallmentsPath &&
      (paymentMethod === 'installments' ||
        paymentMethod === 'miles_installments' ||
        paymentMethod === 'defera_capital'),
    [installmentsCheckoutProvider, paymentMethod, isDeferaZeroRefundableInstallmentsPath]
  );

  /** Slice 14 / Zero 21 / Capital 5 — last installment must be on or before (departure − N days). */
  const lastPaymentDaysBeforeDepartureForInstallments = useMemo(() => {
    if (deferaZeroStyleSchedule) return DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
    if (deferaCapitalServiceFeeTier) return DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
    return SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
  }, [deferaZeroStyleSchedule, deferaCapitalServiceFeeTier]);

  // Defera Miles balance, read once the customer is known and again if the
  // server can't hold the Miles at checkout. Deliberately not live: this
  // checkout's own hold would shrink it mid-payment and re-price the trip
  // under the customer. The search page's figure stands in until it loads.
  const [loadedMilesBalance, setLoadedMilesBalance] = useState(null);
  const refetchMilesBalance = useCallback(async () => {
    try {
      const b = await UserPreferences.getMilesBalance();
      setLoadedMilesBalance(Number.isFinite(Number(b)) ? Math.max(0, Math.floor(Number(b))) : 0);
    } catch (e) {
      console.warn('Review: Miles balance read failed (keeping the last figure)', e);
    }
  }, []);
  useEffect(() => {
    if (currentUser?.id) refetchMilesBalance();
    else setLoadedMilesBalance(null);
  }, [currentUser?.id, refetchMilesBalance]);
  const milesBalance = loadedMilesBalance ?? (location.state?.milesBalance || 0);
  /** Miles the customer chose to spend here: 0 until they apply them (the
   *  default — nothing comes off the price unasked), null for everything this
   *  trip allows, or the amount they typed. */
  const [milesChoice, setMilesChoice] = useState(0);
  const [reserveBalanceCents, setReserveBalanceCents] = useState(location.state?.reserveBalanceCents || 0);
  // Defera Credit (house credit) — display-only here. The backend remains the
  // source of truth for the actual charge (it commits + nets at checkout); we
  // send the gross amounts unchanged and only surface how credit will apply.
  const [deferaCreditCents, setDeferaCreditCents] = useState(0);
  /**
   * How much of the balance the customer wants to spend here. `null` means
   * "all of it" — the default, and what checkout always did.
   *
   * The same credit was already partially spendable on a trip they ALREADY
   * had (Apply Credits takes an amount and previews what it covers), while
   * checkout was all-or-nothing. Same money, two different products, and
   * nobody chose that (2026-09-19). Reasons to hold some back are ordinary:
   * saving it for a pricier trip, or keeping a buffer rather than sinking it
   * into a deposit.
   */
  const [creditSpendCents, setCreditSpendCents] = useState(null);
  const [creditEditing, setCreditEditing] = useState(false);
  const [creditInput, setCreditInput] = useState('');
  const reserveTripId = location.state?.reserveTripId || null;
  const [isReserveBooking, setIsReserveBooking] = useState(false);
  const [reserveShortfall, setReserveShortfall] = useState(0);
  const [isProcessingReserve, setIsProcessingReserve] = useState(false);
  // Reserve booking intent: a single UUID that flows through the whole
  // shortfall → debit → booking pipeline as a shared idempotency key.
  // Created lazily the first time the user tries to pay, so we don't spam
  // intent rows for users who open Review but never check out.
  const reserveIntentIdRef = useRef(null);
  /** The trip total (cents) the intent in reserveIntentIdRef was saved with: the server debits exactly that. */
  const reserveIntentTotalRef = useRef(null);
  const reserveIntentInflightRef = useRef(null);


  const updateAbandonedTripStatus = useCallback(async (status, stepReached = null) => {
    if (!abandonedTripId) return;

    try {
      const updateData = {
        status,
        last_activity: new Date().toISOString()
      };

      if (stepReached) {
        updateData.step_reached = stepReached;
      }

      await AbandonedTrip.update(abandonedTripId, updateData);
    } catch (error) {
      console.error('Error updating abandoned trip status:', error);
    }
  }, [abandonedTripId]);

  /** The search the fare was priced for, saved on the cart as _searchParams so
   *  a restored checkout (and the server's traveler-count check) still knows
   *  the travelers. Read through a ref: trackAbandonedTrip is memoized once. */
  const searchForCartRef = useRef(null);
  searchForCartRef.current = location.state?.searchParams || contextSearchParams || null;

  /**
   * A checkout restored without navigation re-opens the cart it came from:
   * its id, credits, promo and discount, written as nothing. True when the
   * session's cart holds this flight; false sends the caller to create one
   * (a guest who just signed in can't read the cart they built as a guest).
   */
  const adoptSavedCart = useCallback(async (flightData) => {
    try {
      const store = await getCartStore();
      const { data: cart } = await store.latestBySession(contextSessionIdRef.current);
      if (!cart?.id) return false;
      const savedFlightId = cart.flight_data?.id;
      if (savedFlightId && flightData?.id && savedFlightId !== flightData.id) return false;
      const persistedCredits = cart.flight_data?.credits;
      if (Array.isArray(persistedCredits) && persistedCredits.length > 0) setAppliedCredits(persistedCredits);
      if (cart.flight_data?.promo_applied) setPromoApplied(true);
      setDiscount(readCartDiscount(cart.flight_data));
      setCreditsLoaded(true);
      setAbandonedTripId(cart.id);
      lastTrackedSessionRef.current = contextSessionIdRef.current;
      return true;
    } catch (error) {
      console.error('Could not re-open the saved cart:', error);
      return false;
    }
  }, []);

  const trackAbandonedTrip = useCallback(async (flightData, currentSeatSelections) => {
    // GUARD: Prevent concurrent calls - if one is already in flight, skip
    if (trackInFlightRef.current) {
      console.log('⏳ trackAbandonedTrip already in progress, skipping to prevent race condition');
      return;
    }
    
    try {
      trackInFlightRef.current = true;  // Set lock
      
      let userEmail = null;
      try {
        const user = await UserEntity.me();
        userEmail = user.email;
      } catch (userError) {
        console.warn('Could not retrieve user details for abandoned trip tracking:', userError);
      }

      // CRITICAL: Prefer location.state.sessionId (cart's session from navigation)
      // If location.state has a sessionId, align the context to match it
      // This handles dashboard resume, deep links, and any entry that provides a cart session
      const providedSessionId = location.state?.sessionId;
      const contextSessionId = contextSessionIdRef.current;

      if (providedSessionId && providedSessionId !== contextSessionId) {
        console.log('📍 Aligning context sessionId to match cart:', contextSessionId, '→', providedSessionId);
        // CRITICAL: AWAIT alignment to ensure rehydration completes before proceeding
        // This prevents race conditions where stale data syncs to the new session
        await alignSessionId(providedSessionId);
      }
      
      // Use the most authoritative sessionId available:
      // 1. location.state.sessionId (from navigation/resume)
      // 2. contextSessionId (from aligned context or localStorage)
      // 3. Generate new (last resort - shouldn't happen in normal flow)
      const sessionId = providedSessionId || contextSessionId || `session_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      console.log('📍 trackAbandonedTrip using sessionId:', sessionId, {
        fromLocationState: !!providedSessionId,
        fromContext: !providedSessionId && !!contextSessionId,
        generated: !providedSessionId && !contextSessionId
      });

      // Calculate seat total cost for tracking, now handling new nested structure
      const seatTotalCostForTracking = (Array.isArray(currentSeatSelections) ? currentSeatSelections : [] // Ensure it's an array of slices
      ).flatMap((segmentSeats) => Object.values(segmentSeats || {})) // Flatten segments to get individual seat data
        .reduce((total, seat) => {
          // Assuming seat.price can either be a direct number or an object with an 'amount' property
          const priceValue = typeof seat.price === 'object' && seat.price !== null && 'amount' in seat.price ?
            parseFloat(seat.price.amount) :
            parseFloat(seat.price || 0); // Fallback to 0 if price is null/undefined
          return total + priceValue;
        }, 0);

      // CRITICAL FIX: Include hotel price in base price for Flights + Stays packages
      // Prefer the specifically selected room rate over the property's cheapest
      // rate (search results write `hotel.total_amount = cheapest_rate_total_amount`,
      // which does NOT reflect the room the customer actually picked in
      // RoomRatesModal — see 7P4QQC-class report where Cosmopolitan's Lanai suite
      // was being financed at the standard room rate).
      const flightPrice = parseFloat(flightData.total_amount);
      const hotelPrice = selectedHotel
        ? parseFloat(selectedHotel.selectedRate?.total_amount ?? selectedHotel.total_amount ?? 0)
        : 0;
      const basePrice = flightPrice + hotelPrice;
      
      // For Miles-only payment: NO fees, only base fare + seats
      // For other payment methods: include service fees and hold fees
      let serviceFee = 0;
      let holdFee = 0;
      let totalAmount = 0;
      
      if (paymentMethod === 'miles') {
        // Miles-only: base fare + seats, NO FEES
        totalAmount = basePrice + seatTotalCostForTracking;
        console.log('💰 Miles-only cart - base fare only, no fees:', { 
          flightPrice, 
          hotelPrice, 
          basePrice, 
          seatCosts: seatTotalCostForTracking, 
          totalAmount 
        });
      } else if (paymentMethod === 'reserve') {
        // Reserve: 11% service fee on (base + seats), NO hold fee
        serviceFee = (selectedHotel && packageType === 'flight_stay') ? 0 : (basePrice + seatTotalCostForTracking) * 0.11;
        totalAmount = basePrice + serviceFee + seatTotalCostForTracking;
      } else {
        // Installments or Miles+Installments: include all fees (waive for flight+hotel)
        const capitalFee =
          installmentsCheckoutProvider === 'defera_capital' &&
          !isDeferaZeroRefundableInstallmentsPath &&
          (paymentMethod === 'installments' || paymentMethod === 'miles_installments' || paymentMethod === 'defera_capital');
        // Service fee on (base + seats), matching calculateReviewPagePricing
        serviceFee = (selectedHotel && packageType === 'flight_stay') ? 0 : calculateServiceFee(basePrice + seatTotalCostForTracking, capitalFee);
        // Seats are financed: inside the held subtotal, so the hold fee
        // applies to them (matches calculateReviewPagePricing).
        const subtotal = basePrice + serviceFee + seatTotalCostForTracking;
        const holdRate = capitalFee ? DEFERA_CAPITAL_HOLD_FEE_RATE : 0.15;
        holdFee = subtotal * holdRate;
        totalAmount = subtotal + holdFee;
      }

      const firstSlice = flightData.slices?.[0];

      // Extract IATA codes and convert to city names using getCityName utility
      const firstSegment = firstSlice?.segments?.[0];
      const lastSegment = firstSlice?.segments?.[firstSlice?.segments?.length - 1];

      // Get IATA codes from segments (most reliable source)
      const originCode = firstSegment?.origin?.iata_code;
      const destinationCode = lastSegment?.destination?.iata_code;

      // Convert IATA codes to actual city names
      // If getCityName returns the same code (not in map), fall back to city_name or name
      let departureCity = 'Unknown';
      if (originCode) {
        const cityFromCode = getCityName(originCode);
        if (cityFromCode !== originCode) {
          // Successfully mapped to city name
          departureCity = cityFromCode;
        } else {
          // Code not in map, use city_name or name as fallback
          departureCity = firstSegment?.origin?.city_name || firstSegment?.origin?.name || originCode;
        }
      }

      let arrivalCity = 'Unknown';
      if (destinationCode) {
        const cityFromCode = getCityName(destinationCode);
        if (cityFromCode !== destinationCode) {
          // Successfully mapped to city name
          arrivalCity = cityFromCode;
        } else {
          // Code not in map, use city_name or name as fallback
          arrivalCity = lastSegment?.destination?.city_name || lastSegment?.destination?.name || destinationCode;
        }
      }

      const departureDate = firstSegment?.departing_at?.split('T')[0] || new Date().toISOString().split('T')[0];
      const tripType = flightData.slices?.length > 1 ? 'round_trip' : 'one_way';
      const marketingAirline = firstSegment?.marketing_carrier?.name || 'Unknown';

      // Transform currentSeatSelections into an object for storage
      const seatSelectionsObjectForTracking = (Array.isArray(currentSeatSelections) ? currentSeatSelections : []).reduce((acc, currentSegmentSeats, idx) => {
        if (currentSegmentSeats && Object.keys(currentSegmentSeats).length > 0) {
          acc[`segment_${idx}`] = currentSegmentSeats;
        }
        return acc;
      }, {});

      // CRITICAL: Generate booking_reference from offer_id and store in flight_data JSONB
      // This bypasses PostgREST schema cache issues with the booking_reference column
      // Use last 6 chars of offer_id (same logic as EditCart.jsx)
      const bookingReference = flightData?.id 
        ? flightData.id.slice(-6).toUpperCase() 
        : `DEF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      // Store booking_reference inside flight_data JSONB to avoid schema cache errors.
      // Seat-map picks ride along in flight_data so the cart → booking push
      // (EditCart) inherits them without further changes.
      const flightDataWithBookingRef = {
        ...flightData,
        defera_booking_reference: bookingReference,
        ...buildSeatDataForFlightData(currentSeatSelections, flightData),
        ...(searchForCartRef.current ? { _searchParams: searchForCartRef.current } : {})
      };
      
      // Calculate miles_to_deplete for miles and miles+installments modes
      const milesNeeded = dollarsToMiles(totalAmount);
      const milesToDeplete = ((paymentMethod === 'miles' || paymentMethod === 'miles_installments') && milesBalance > 0) 
        ? Math.min(milesBalance, milesNeeded)
        : 0;

      // SERVER-SIDE VALIDATION: Prevent duplicate miles bookings (both miles and miles_installments)
      // Wrap in try-catch to avoid blocking trip creation if validation check fails
      if (paymentMethod === 'miles' || paymentMethod === 'miles_installments') {
        try {
          const { data: pendingCarts, error: checkError } = await supabase
            .from('abandoned_trips')
            .select('id')
            .eq('user_email', userEmail)
            .eq('status', 'ready_for_push')
            .or(`payment_method.eq.miles,payment_method.eq.miles_installments`)
            .limit(1);

          if (checkError) {
            console.warn('Unable to check for pending miles carts (non-blocking):', checkError);
            // Continue with trip creation - validation check failed but we don't want to block the user
          } else if (pendingCarts && pendingCarts.length > 0) {
            console.warn('User attempted duplicate miles booking with pending cart:', { 
              userEmail, 
              pendingCartId: pendingCarts[0].id,
              attemptedPaymentMethod: paymentMethod 
            });
            throw new Error('You have a pending miles booking awaiting confirmation. Please complete that booking before using miles again.');
          }
        } catch (validationError) {
          // If it's our custom error about duplicate booking, re-throw it
          if (validationError.message.includes('pending miles booking')) {
            throw validationError;
          }
          // Otherwise, log and continue (don't block trip creation)
          console.warn('Miles validation check failed (non-blocking):', validationError);
        }
      }

      const abandonedTrip = await AbandonedTrip.create({
        session_id: sessionId,
        user_email: userEmail,
        flight_data: flightDataWithBookingRef,
        seat_selections: seatSelectionsObjectForTracking,
        stay_data: selectedHotel || null,
        total_amount: totalAmount,
        departure_city: departureCity,
        arrival_city: arrivalCity,
        departure_date: departureDate,
        trip_type: tripType,
        marketing_airline: marketingAirline,
        status: 'in_cart',
        last_activity: new Date().toISOString(),
        step_reached: 'review',
        payment_method: paymentMethod,
        miles_to_deplete: milesToDeplete
      });
      
      console.log('✓ Cart created with booking reference:', bookingReference, 'Cart ID:', abandonedTrip.id);
      console.log('✓ Booking reference stored in flight_data.defera_booking_reference:', flightDataWithBookingRef.defera_booking_reference);
      
      // Verify booking reference was saved correctly
      if (flightDataWithBookingRef.defera_booking_reference !== bookingReference) {
        console.error('⚠️ BOOKING REFERENCE MISMATCH: Expected', bookingReference, 'but got', flightDataWithBookingRef.defera_booking_reference);
      }

      // CRITICAL: Load persisted credits from returned cart immediately (before separate effect runs)
      // This ensures credits survive page refresh by loading them during cart hydration
      const persistedCredits = abandonedTrip.flight_data?.credits;
      if (persistedCredits && Array.isArray(persistedCredits) && persistedCredits.length > 0) {
        console.log('✅ Restoring credits from cart during hydration:', persistedCredits.length, 'credits');
        setAppliedCredits(persistedCredits);
      }
      if (abandonedTrip.flight_data?.promo_applied) {
        setPromoApplied(true);
      }
      setDiscount(readCartDiscount(abandonedTrip.flight_data));
      // Mark credits as loaded to gate SlicePay auto-init
      setCreditsLoaded(true);

      setAbandonedTripId(abandonedTrip.id);
      lastTrackedSessionRef.current = sessionId;  // Track successfully tracked session
    } catch (error) {
      console.error('Error tracking abandoned trip:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    } finally {
      trackInFlightRef.current = false;  // Release lock in all cases
    }
  }, [setAbandonedTripId]);

  // CRITICAL: Calculate pricing BEFORE callbacks that reference them (fixes TDZ)
  // True when ANY seat was picked, even $0 ones — drives the Selected Seats
  // card and the breakdown line so free seats don't silently disappear.
  const hasSelectedSeats = seatSelections.some(
    (segmentSelections) => segmentSelections && Object.keys(segmentSelections).length > 0
  );
  const seatTotalCost = seatSelections.reduce((total, segmentSelections) => {
    if (!segmentSelections) return total;
    return total + Object.values(segmentSelections).reduce((segTotal, seat) => {
      const priceValue = typeof seat.price === 'object' && seat.price !== null && 'amount' in seat.price ?
        parseFloat(seat.price.amount) :
        parseFloat(seat.price || 0);
      return segTotal + priceValue;
    }, 0);
  }, 0);

  const basePrice = selectedFlight ? parseFloat(selectedFlight.total_amount) : 0;
  // Prefer the specifically selected room rate — see note above in the tracking
  // effect. `selectedHotel.total_amount` is the property's cheapest rate from
  // search results; the customer's actual pick lives on `selectedRate`.
  const hotelPrice = selectedHotel
    ? parseFloat(selectedHotel.selectedRate?.total_amount ?? selectedHotel.total_amount ?? 0)
    : 0;
  const combinedBasePrice = basePrice + hotelPrice;
  
  // Calculate total credits applied FIRST (needed for pricing calculation)
  const creditsAppliedTotal = appliedCredits.reduce((sum, c) => sum + c.claimedAmount, 0);
  
  // CRITICAL: Credits are applied to BASE FARE first, then fees are calculated on the reduced amount
  /** The fare, seats, fees and hold, before any discount code or Miles. */
  const baseTravelPricing = useMemo(() => {
    if (!selectedFlight) return null;
    return calculateReviewPagePricing(
      combinedBasePrice,
      seatTotalCost,
      effectivePaymentMethod,
      milesBalance,
      creditsAppliedTotal,
      !!(selectedHotel && packageType === 'flight_stay') || promoApplied, // Waive service fee for flight+hotel or promo
      installmentsCheckoutProvider
    );
  }, [selectedFlight, combinedBasePrice, seatTotalCost, effectivePaymentMethod, milesBalance, creditsAppliedTotal, selectedHotel, packageType, promoApplied, installmentsCheckoutProvider]);

  // A discount code and Defera Miles both come off the GRAND TOTAL, but every
  // financed rail bills the pre-hold subtotal and adds its hold on top, so each
  // reduction is grossed down by the hold rate or the customer quietly gets it
  // plus 15% (applyCheckoutDiscounts in src/utils/discountCodes.js). The
  // checkout functions re-derive the price the same way, in the same order:
  // the code first, then the Miles.
  //
  // Folded into `pricing` itself, deliberately: every downstream consumer
  // (the Slice Pay amount, the Capital schedule, Reserve's required balance,
  // the displayed total) reads from this object, so one insertion here keeps
  // them all consistent instead of relying on a dozen call sites remembering
  // to subtract.
  const discountCentsForPricing = discount && discount.cents > 0 ? discount.cents : 0;
  /** The travel pricing with any discount code, before Miles: what the Miles cap is measured against. */
  const travelPricingBeforeMiles = useMemo(() => {
    if (!baseTravelPricing) {
      return {
        serviceFee: 0,
        holdFee: 0,
        grandTotalIncludingSeats: 0,
        seatTotal: 0,
        milesUsed: 0,
        milesValue: 0,
        cashPortion: 0,
        subtotalBeforeHold: 0,
        totalExcludingSeats: 0,
        creditsApplied: 0,
        baseFareAfterCredits: 0
      };
    }
    if (discountCentsForPricing === 0) return baseTravelPricing;
    return {
      ...applyCheckoutDiscounts(baseTravelPricing, { discountCents: discountCentsForPricing }),
      discountCode: discount.code,
    };
  }, [baseTravelPricing, discountCentsForPricing, discount]);

  // ── Defera Miles ─────────────────────────────────────────────────────────
  // Worth 1.25¢ each off a trip (gift cards: 1¢), on the rails whose checkout
  // functions hold them: Defera Capital (Stripe or NMI) and Defera Zero. Shown
  // like a payment method, above the card (MilesCheckoutPanel): nothing comes
  // off until the customer applies them, all the trip allows or an amount
  // they type (supabase/functions/_shared/miles-redemption.js). Miles can pay
  // for the whole trip, and then it's booked with Miles alone (no card, no
  // plan: MilesOnlyCheckout); short of that, $25 stays on the plan. Miles-only
  // isn't offered on a Flight + Hotel package, a legacy promo, with airline
  // credits, or under CAPITAL_MIN_LEAD_DAYS out (server/miles-only-booking.js
  // refuses the same). Travel insurance and Miles never combine (Terms 2.17):
  // whichever the customer picks first sets the other aside.
  const milesRailSupported = isDeferaCapitalCheckout || deferaZeroStyleSchedule;
  const milesTripTotalCents = Math.round((travelPricingBeforeMiles.grandTotalIncludingSeats || 0) * 100);
  const milesBlockedByInsurance = Boolean(insuranceChoice?.tier);
  const milesDepartureDay = String(selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at || '').slice(0, 10);
  const milesLeadDays = /^\d{4}-\d{2}-\d{2}$/.test(milesDepartureDay)
    ? Math.round((Date.parse(`${milesDepartureDay}T00:00:00Z`) - Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`)) / 86400000)
    : null;
  const milesAllowWholeTrip = milesRailSupported && !isFlightHotelPackage && !promoApplied
    && !(creditsAppliedTotal > 0) && milesLeadDays !== null && milesLeadDays >= CAPITAL_MIN_LEAD_DAYS;
  const milesMax = milesRailSupported && currentUser?.id && !milesBlockedByInsurance
    ? maxCheckoutMiles({ balance: milesBalance, tripTotalCents: milesTripTotalCents, allowWholeTrip: milesAllowWholeTrip })
    : 0;
  const milesToUse = milesMax > 0
    ? checkoutMilesToUse({ requested: milesChoice, balance: milesBalance, tripTotalCents: milesTripTotalCents, allowWholeTrip: milesAllowWholeTrip })
    : 0;
  /** The Miles pay for the whole trip: booked with Miles alone, no plan. */
  const milesCoverWholeTrip = milesAllowWholeTrip && milesToUse > 0 && coversWholeTrip(milesToUse, milesTripTotalCents);

  /** The travel pricing: fare, seats, fees, hold, discount code and Miles. A
   *  Faye premium is added on top of this below (`pricing`) and never feeds
   *  the fees, the discounts or the deposit. */
  const travelPricing = useMemo(() => {
    if (!baseTravelPricing || milesToUse <= 0) return travelPricingBeforeMiles;
    return {
      ...applyCheckoutDiscounts(baseTravelPricing, {
        discountCents: discountCentsForPricing,
        milesDiscountCents: tripCentsForMiles(milesToUse),
      }),
      ...(discountCentsForPricing > 0 ? { discountCode: discount.code } : {}),
      // Miles that pay for the whole trip leave exactly nothing: the
      // subtraction above can land a cent either side of zero.
      ...(milesCoverWholeTrip ? {
        subtotalBeforeHold: 0,
        holdFee: 0,
        grandTotalIncludingSeats: 0,
        totalExcludingSeats: 0,
        milesApplied: travelPricingBeforeMiles.grandTotalIncludingSeats,
      } : {}),
      checkoutMiles: milesToUse,
    };
  }, [baseTravelPricing, travelPricingBeforeMiles, milesToUse, milesCoverWholeTrip, discountCentsForPricing, discount]);
  /** What a number of Miles really takes off this trip's total, in cents: the
   *  hold's rounding can move it a cent from their face value, and the Miles
   *  card quotes the same figure the total will show. */
  const milesValueCents = useCallback((miles) => {
    if (!baseTravelPricing || !(miles > 0)) return 0;
    const priced = applyCheckoutDiscounts(baseTravelPricing, {
      discountCents: discountCentsForPricing,
      milesDiscountCents: tripCentsForMiles(miles),
    });
    return Math.round((priced.milesApplied || 0) * 100);
  }, [baseTravelPricing, discountCentsForPricing]);
  /** Defera Miles, shown like a payment method above the card (and above the
   *  under-$1 notice, so Miles that pay the whole trip stay reachable). */
  const milesWalletRow = milesRailSupported && currentUser?.id && milesBalance > 0 ? (
    <MilesCheckoutPanel
      milesBalance={milesBalance}
      milesMax={milesMax}
      milesToUse={milesToUse}
      appliedCents={Math.round((travelPricing.milesApplied || 0) * 100)}
      coversWholeTrip={milesCoverWholeTrip}
      tripTotalCents={milesTripTotalCents}
      allowWholeTrip={milesAllowWholeTrip}
      valueCentsFor={milesValueCents}
      unavailableReason={milesBlockedByInsurance ? 'Defera Miles can’t be combined with travel insurance.' : null}
      onChoose={setMilesChoice}
    />
  ) : null;

  // ── Faye travel insurance (launch switch: src/utils/fayeFlag.js) ─────────
  // Offered on Defera Capital trips with an $800+ base fare once every
  // traveler's birthdate is in. Priced on the travel total; the premium is
  // added AFTER Defera's fees and rides on the payments after the deposit
  // (supabase/functions/_shared/faye-checkout.js). The server re-prices it.
  const fayeOffer = useMemo(() => {
    if (!FAYE_INSURANCE_ENABLED || insuranceUnavailable || !selectedFlight) return null;
    const departureDate = selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at;
    if (!departureDate) return null;
    // How many payments follow the deposit; the premium doesn't change that.
    const travelSchedule = calculatePaymentSchedule({
      bookingDate: new Date(),
      departureDate,
      totalWithFee: travelPricing.subtotalBeforeHold,
      depositPercentage: selectedDeposit,
      daysBeforeDepartureForLastPayment: DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
      holdFeeRate: DEFERA_CAPITAL_HOLD_FEE_RATE,
      deferaCapitalDepositTiers: true,
    });
    const plan = selectedPaymentPlan === 'biweekly' ? travelSchedule?.biweeklyPlan : travelSchedule?.weeklyPlan;
    return buildFayeOffer({
      enabled: true,
      paymentMethod: effectivePaymentMethod,
      flightData: selectedFlight,
      travelers: passengers,
      baseFare: basePrice,
      // To the cent, like the server: it re-prices on the total it is sent,
      // and Faye rounds each traveler, so a fraction of a cent here could move
      // a party's premium by a cent per traveler and fail the re-price.
      tripCost: Math.round(travelPricing.grandTotalIncludingSeats * 100) / 100,
      paymentsAfterDeposit: travelSchedule?.available && plan?.available ? plan.paymentCount : 0,
      // UTC, like the server's re-price: a birthday can't flip between them.
      today: new Date().toISOString().slice(0, 10),
      timezoneForAirport,
    });
  // fayeQuoteKey re-prices on today's date after checkout refuses the plan.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlight, travelPricing, selectedDeposit, selectedPaymentPlan, effectivePaymentMethod, passengers, basePrice, insuranceUnavailable, fayeQuoteKey]);
  const insurancePlan = fayeOffer?.eligible && insuranceChoice?.tier
    ? fayeOffer.plans.find((p) => p.tier === insuranceChoice.tier) || null
    : null;
  /** Checkout refused the travel insurance (its price changed, or it isn't
   *  available): take the plan off so the customer can choose again on a
   *  fresh price, or carry on without it. Nothing was charged. */
  const handleInsuranceRefused = useCallback((data) => {
    setInsuranceChoice(null);
    if (data?.error_type === 'insurance_unavailable') setInsuranceUnavailable(true);
    setFayeQuoteKey((k) => k + 1);
  }, []);
  /** Checkout couldn't hold the Miles (the balance changed since this page
   *  loaded): read the balance again and go back to the default, so the trip
   *  is re-priced on what the customer really has. Nothing was charged. */
  const handleMilesRefused = useCallback(() => {
    setMilesChoice(0);
    refetchMilesBalance();
  }, [refetchMilesBalance]);
  const insurancePremium = insurancePlan ? insurancePlan.premiumCents / 100 : 0;
  /** What the customer pays: the travel pricing plus any Faye premium. Every
   *  total downstream reads this; the fees, the discount and the deposit come
   *  from the travel figures, which the premium never changes. */
  const pricing = useMemo(() => (insurancePremium > 0
    ? {
      ...travelPricing,
      grandTotalIncludingSeats: Math.round((travelPricing.grandTotalIncludingSeats + insurancePremium) * 100) / 100,
      insurancePremium,
    }
    : travelPricing), [travelPricing, insurancePremium]);

  // Amount we send to Slice Pay = cashPortion + serviceFee + seat fees (Slice Pay adds 15% hold fee on top).
  // Seats are FINANCED: included here so they're collected across the installment schedule.
  // Use subtotalBeforeHold from pricing when available to ensure service fee + seats are always included
  const totalBeforeHoldFee = pricing.subtotalBeforeHold ?? ((pricing.cashPortion ?? pricing.baseFareAfterCredits ?? combinedBasePrice ?? 0) + (pricing.serviceFee || 0) + (pricing.seatTotal || 0));

  /** Slice Pay–style schedule for Defera Capital (same calculator as installments / Slice).
   *  Must pass `deferaCapitalDepositTiers: true` so the DC-only tiers (5/10% at 90+ day
   *  lead, etc.) are honored — without it the calculator snaps a 5% selection up to the
   *  Slice Pay floor (20%), corrupting `capitalInstallmentInfo` and the
   *  `client_payment_schedule` we hand off to `DeferaZeroCheckoutForm`. */
  /**
   * The plan the Defera Credit preview is measured against.
   *
   * Covers Defera Zero as well as Capital. Zero is 0% down, so there is no
   * deposit for credit to land on and the whole balance rides the installments —
   * but the arguments here are otherwise the ones the rendered `PaymentSchedule`
   * below already receives for Zero (subtotalBeforeHold, 7-day first payment,
   * Slice-style hold rate, no Capital deposit tiers). Feeding the same
   * calculator the same inputs is what keeps the previewed split identical to
   * the schedule the customer is looking at.
   */
  const capitalSliceSchedule = useMemo(() => {
    const forZero = deferaZeroStyleSchedule;
    if ((!isDeferaCapitalCheckout && !forZero) || !selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at) return null;
    const departureDate = selectedFlight.slices[0].segments[0].departing_at;
    return calculatePaymentSchedule({
      bookingDate: new Date(),
      departureDate,
      totalWithFee: forZero ? pricing.subtotalBeforeHold : totalBeforeHoldFee,
      depositPercentage: forZero ? 0 : selectedDeposit,
      firstPaymentDays: forZero ? 7 : undefined,
      daysBeforeDepartureForLastPayment: forZero
        ? lastPaymentDaysBeforeDepartureForInstallments
        : DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
      holdFeeRate: forZero ? SLICE_PAY_HOLD_FEE_RATE : DEFERA_CAPITAL_HOLD_FEE_RATE,
      deferaCapitalDepositTiers: !forZero,
      insurancePremium: forZero ? 0 : insurancePremium,
    });
  }, [
    isDeferaCapitalCheckout,
    deferaZeroStyleSchedule,
    selectedFlight,
    totalBeforeHoldFee,
    pricing.subtotalBeforeHold,
    selectedDeposit,
    lastPaymentDaysBeforeDepartureForInstallments,
    insurancePremium,
  ]);

  const capitalInstallmentInfo = useMemo(() => {
    if (!capitalSliceSchedule?.available) return null;
    const plan = selectedPaymentPlan === 'weekly' ? capitalSliceSchedule.weeklyPlan : capitalSliceSchedule.biweeklyPlan;
    if (!plan?.available) return null;
    return { count: plan.paymentCount, amountCents: Math.round(plan.paymentAmount * 100) };
  }, [capitalSliceSchedule, selectedPaymentPlan]);

  // Defera Credit balance — fetched once on any installment checkout (Capital
  // AND Zero) so we can preview how it applies. No-op (and renders nothing) when
  // the customer has none. Zero was excluded until 2026-08-20, which meant a
  // customer holding credit reached /review and saw no trace of it.
  useEffect(() => {
    if (!isDeferaCapitalCheckout && !deferaZeroStyleSchedule) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(deferaCreditUrl('balance'), {
          headers: mergeSupabaseEdgeHeaders({ 'Authorization': `Bearer ${session.access_token}` }),
          credentials: 'omit',
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setDeferaCreditCents(data.balance_cents || 0);
        }
      } catch (e) { /* leave at 0 — credit is purely additive */ }
    })();
    return () => { cancelled = true; };
  }, [isDeferaCapitalCheckout, deferaZeroStyleSchedule]);

  // How the available credit spreads across this plan (deposit first, then
  // installments front-to-back). Mirrors the server-side allocator exactly —
  // both rails call the same `allocateDeferaCredit`, and Zero simply arrives
  // with a $0 deposit so every cent lands on installments.
  /** What will actually be spent: their choice, clamped to what they have. */
  const effectiveCreditCents = useMemo(
    () => (creditSpendCents === null
      ? deferaCreditCents
      : Math.max(0, Math.min(deferaCreditCents, creditSpendCents))),
    [creditSpendCents, deferaCreditCents],
  );

  const capitalCreditAllocation = useMemo(() => {
    if ((!isDeferaCapitalCheckout && !deferaZeroStyleSchedule) || deferaCreditCents <= 0) return null;
    const depositCents = Math.round((capitalSliceSchedule?.depositAmount || 0) * 100);
    const info = capitalInstallmentInfo;
    if (depositCents <= 0 && !info) return null;
    const installments = info
      ? Array.from({ length: info.count }, (_, i) => ({ installment_number: i + 1, amount_cents: info.amountCents }))
      : [];
    const alloc = allocateDeferaCredit({ depositCents, installments, availableCreditCents: effectiveCreditCents });
    if (alloc.total_credit_consumed_cents <= 0) return null;
    return alloc;
  }, [isDeferaCapitalCheckout, deferaZeroStyleSchedule, deferaCreditCents, effectiveCreditCents, capitalSliceSchedule, capitalInstallmentInfo]);

  // Reserve: fresh balance on mount + shortfall calculation
  useEffect(() => {
    if (paymentMethod !== 'reserve') return;
    const fetchBalance = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(reserveUrl('balance'), {
          headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
        });
        if (res.ok) {
          const data = await res.json();
          setReserveBalanceCents(data.balance_cents || 0);
        }
      } catch (e) { /* keep existing value */ }
    };
    fetchBalance();
  }, [paymentMethod]);

  useEffect(() => {
    if (paymentMethod !== 'reserve' || !selectedFlight) return;
    const totalNeededCents = Math.round(pricing.grandTotalIncludingSeats * 100);
    const shortfall = Math.max(0, totalNeededCents - reserveBalanceCents);
    setReserveShortfall(shortfall);
  }, [paymentMethod, pricing.grandTotalIncludingSeats, reserveBalanceCents, selectedFlight]);

  /**
   * Lazily create a reserve_booking_intents row. Returns the intent_id (UUID) on success,
   * or `null` when it couldn't be saved; the caller then stops before any money moves,
   * because the server debits Reserve only against an intent (server/reserve-booking-debit.js).
   * Never throws.
   */
  const ensureReserveBookingIntent = useCallback(async () => {
    // The server debits the intent's own total, so an intent saved before the
    // total changed (a seat added, a code applied) is replaced, not reused.
    const currentTotalCents = Math.round(pricing.grandTotalIncludingSeats * 100);
    if (reserveIntentIdRef.current && reserveIntentTotalRef.current === currentTotalCents) {
      return reserveIntentIdRef.current;
    }
    reserveIntentIdRef.current = null;
    if (reserveIntentInflightRef.current) return reserveIntentInflightRef.current;

    const inflight = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return null;
        if (!selectedFlight) return null;

        const totalNeededCents = Math.round(pricing.grandTotalIncludingSeats * 100);
        const offerId = selectedFlight?.id ?? selectedFlight?.offer_id ?? null;

        const body = {
          offer_id: offerId,
          flight_data: {
            ...selectedFlight,
            ...(promoApplied && { promo_applied: true }),
            ...buildBookingDiscountFields(discount)
          },
          passengers: passengers || [],
          session_id: abandonedTripId || null,
          total_amount_cents: totalNeededCents,
          base_fare_cents: Math.round(combinedBasePrice * 100),
          service_fee_cents: Math.round((pricing.serviceFee || 0) * 100),
          shortfall_cents: Math.max(0, totalNeededCents - reserveBalanceCents),
          reserve_balance_at_start_cents: reserveBalanceCents
        };

        const res = await fetch('/api/reserve/booking-intent', {
          method: 'POST',
          // Act-as: the intent belongs to the customer whose Reserve is debited.
          headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }),
          body: JSON.stringify(body)
        });
        // Down or refused: null, and the checkout stops with a retry message.
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.intent_id) {
          if (typeof console !== 'undefined') console.warn('[Review] booking-intent create failed', res.status, json?.error);
          return null;
        }
        reserveIntentIdRef.current = json.intent_id;
        reserveIntentTotalRef.current = totalNeededCents;
        return json.intent_id;
      } catch (err) {
        if (typeof console !== 'undefined') console.warn('[Review] booking-intent fetch threw', err?.message);
        return null;
      }
    })();

    reserveIntentInflightRef.current = inflight;
    try {
      return await inflight;
    } finally {
      reserveIntentInflightRef.current = null;
    }
  }, [
    selectedFlight,
    pricing.grandTotalIncludingSeats,
    pricing.serviceFee,
    combinedBasePrice,
    reserveBalanceCents,
    abandonedTripId,
    passengers,
    promoApplied,
    discount
  ]);

  const stampReserveIntent = useCallback(async (intentId, patch) => {
    if (!intentId) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(`/api/reserve/booking-intent/${intentId}/update`, {
        method: 'POST',
        headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(patch)
      });
    } catch (_) { /* non-fatal, resume endpoint can self-heal from Stripe + ledger state */ }
  }, []);

  // Debit Reserve + create booking. Called when shortfall is 0, or after shortfall payment completes.
  // reserveCashPaymentCents: optional, when user paid shortfall by card (from ReserveShortfallPaymentForm)
  // intentIdOverride: optional, passed from the shortfall form so we reuse the same intent UUID
  //                   it used as the Stripe idempotency key.
  const handleReserveDebitAndCreateBooking = useCallback(async (reserveCashPaymentCents = 0, intentIdOverride = null) => {
    if (!selectedFlight || isProcessingReserve) return;
    setIsProcessingReserve(true);

    try {
      const totalNeededCents = Math.round(pricing.grandTotalIncludingSeats * 100);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Please sign in'); setIsProcessingReserve(false); return; }

      // The booking intent is the checkout: the server debits ITS total and
      // the booking row carries its id. Without one there is nothing to debit
      // against (the server refuses a debit that names no intent).
      let intentId = intentIdOverride || null;
      if (!intentId) {
        try {
          intentId = await ensureReserveBookingIntent();
        } catch (_) {
          intentId = null;
        }
      }
      if (!intentId) {
        toast.error("We couldn't start your checkout. Please try again.");
        setIsProcessingReserve(false);
        return;
      }
      reserveIntentIdRef.current = intentId;

      const slices = selectedFlight.slices || [];
      const firstSegment = slices[0]?.segments?.[0];
      const lastSegOfFirstSlice = slices[0]?.segments?.slice(-1)[0];
      const tripDesc = firstSegment
        ? `${firstSegment.origin?.iata_code || '???'} → ${lastSegOfFirstSlice?.destination?.iata_code || '???'}`
        : 'Flight Booking';

      const debitRes = await fetch(reserveUrl('debit-for-booking'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }),
        body: JSON.stringify({
          // The server debits the intent's own total (idempotent on the intent,
          // so retries and an auto-resume after a tab-close never double-debit).
          // amount_cents only has to agree with it.
          intent_id: intentId,
          amount_cents: totalNeededCents,
          description: `Booking: ${tripDesc}`,
          reserve_trip_id: reserveTripId || undefined
        })
      });
      const debitData = await debitRes.json().catch(() => ({}));
      if (!debitData.success) {
        if (debitData.code === 'intent_total_changed') {
          // The page's total moved since the intent was saved: the next try starts a fresh one.
          reserveIntentIdRef.current = null;
          reserveIntentTotalRef.current = null;
        } else {
          await stampReserveIntent(intentId, { last_error: debitData.error || 'Debit failed' });
        }
        toast.error(debitData.error || 'Failed to debit Reserve');
        setIsProcessingReserve(false);
        return;
      }
      // The server marked the intent debited when it posted the debit.

      // Create the booking in the system with Pending status.
      const offerId = selectedFlight?.id ?? selectedFlight?.offer_id ?? `reserve-${Date.now()}`;
      const bookingRef = offerId
        ? offerId.slice(-6).toUpperCase()
        : `DEF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      const reserveBaseFare = combinedBasePrice;
      const reserveServiceFee = pricing.serviceFee;
      // I74BF5: derive the airline's actual fare brand for the bookings.cabin_class
      // column so column-driven readers (ReserveTripsCard.SliceCard, admin all-trips
      // modal, raw SQL queries) match what FlightDetailsModal renders. Falls back
      // to the search-form enum that `transformDuffelOfferFromSerpAPI` parks on
      // the offer's top level — keeps the column non-empty for legacy offers
      // that lack a bookingOption.
      const reserveCabinClass = resolveMarketingFareNameFromOffer(
        selectedFlight,
        selectedFlight?.cabin_class || 'economy',
      );
      const bookingPayload = {
        // Act-as: the trip belongs to the customer whose Reserve paid for it.
        user_id: getReserveActAsUserId() || session.user.id,
        offer_id: offerId,
        booking_reference: bookingRef,
        // Ties the booking to the intent its Reserve debit was posted under.
        // The server unique index on (user_id, client_request_id) also turns
        // double-clicks and retries into the same row.
        client_request_id: intentId,
        cabin_class: reserveCabinClass,
        flight_data: {
          ...selectedFlight,
          ...buildSeatDataForFlightData(seatSelections, selectedFlight),
          defera_booking_reference: bookingRef,
          _payment_method: 'reserve',
          ...(reserveCashPaymentCents > 0 && { _reserve_cash_payment_cents: reserveCashPaymentCents }),
          ...(promoApplied && { promo_applied: true }),
          ...buildBookingDiscountFields(discount)
        },
        status: 'Pending',
        payment_method: 'reserve',
        total_amount: reserveBaseFare.toFixed(2),
        service_fee: reserveServiceFee.toFixed(2),
        hold_fee: '0.00',
        grand_total: (totalNeededCents / 100).toFixed(2),
        grand_total_with_hold: (totalNeededCents / 100).toFixed(2),
        passengers: passengers,
        use_reserve_balance: true,
        session_id: abandonedTripId
      };

      const bookingRes = await fetch('/api/admin/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(bookingPayload)
      });
      const bookingData = await bookingRes.json();

      if (bookingData.error) {
        await stampReserveIntent(intentId, { last_error: bookingData.error });
        toast.error('Booking created but encountered an issue: ' + bookingData.error);
      } else {
        // The server checks the booking row and its debit before closing the intent.
        await stampReserveIntent(intentId, { status: 'booked' });
        toast.success('Booking confirmed! Your Reserve balance has been debited.');
      }

      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Failed to complete Reserve booking');
    } finally {
      setIsProcessingReserve(false);
    }
  }, [selectedFlight, isProcessingReserve, pricing.grandTotalIncludingSeats, pricing.serviceFee, combinedBasePrice, abandonedTripId, passengers, navigate, reserveTripId, ensureReserveBookingIntent, stampReserveIntent, promoApplied, discount, seatSelections]);

  const handleReserveBooking = useCallback(() => {
    if (reserveShortfall > 0) return; // Shortfall handled by ReserveShortfallPaymentForm
    handleReserveDebitAndCreateBooking();
  }, [reserveShortfall, handleReserveDebitAndCreateBooking]);

  const handleInitiateSlicePay = useCallback(async () => {
    if (!selectedFlight || isInitiatingPayment) return;

    // Guard: Require a persisted cart ID before starting payment
    if (!abandonedTripId) {
      console.warn('🛑 SlicePay initiation blocked - cart ID not ready yet');
      return;
    }

    setIsInitiatingPayment(true);
    try {
      const creditsTotal = appliedCredits.reduce((sum, c) => sum + c.claimedAmount, 0);
      // Send totalBeforeHoldFee - Slice Pay adds 15% hold fee, then applies deposit %
      const finalPriceForPayment = totalBeforeHoldFee;
      
      console.log('💳 SlicePay: sending amount (cashPortion + serviceFee, Slice Pay adds 15% hold):', {
        totalBeforeHoldFee: finalPriceForPayment,
        cashPortion: pricing.cashPortion,
        serviceFee: pricing.serviceFee,
        subtotalBeforeHold: pricing.subtotalBeforeHold,
        seatTotal: pricing.seatTotal,
        creditsCount: appliedCredits.length
      });

      // CRITICAL: Pass cart ID so server can fetch booking_reference directly
      const { data: responseData, status } = await initiateSlicePayFlow({
        flight: {
          ...selectedFlight,
          // Pass amount WITHOUT hold fee (SlicePay adds its own 15%)
          total_amount: finalPriceForPayment.toFixed(2), // Ensure it's a string with 2 decimal places
          // Include credits metadata for reconciliation with full fee breakdown
          credits_applied: appliedCredits.length > 0 ? {
            credits: appliedCredits,
            total: creditsTotal,
            original_base_fare: combinedBasePrice,
            base_fare_after_credits: pricing.baseFareAfterCredits,
            service_fee_on_reduced_base: pricing.serviceFee,
            hold_fee_on_reduced_base: pricing.holdFee,
            seat_total: pricing.seatTotal,
            final_total: finalPriceForPayment
          } : null
        },
        cart_id: abandonedTripId, // Pass cart ID to fetch booking_reference from cart
        deposit_percentage: selectedDeposit, // Pass selected deposit percentage
        payment_plan: selectedPaymentPlan // Pass selected payment plan (weekly/biweekly)
      });

      console.log('SlicePay response:', { status, responseData });

      if (status === 200 && responseData.embedded_flow_url) {
        setPaymentLink(responseData.embedded_flow_url);
        console.log('Payment link set:', responseData.embedded_flow_url);
        console.log('Slice Pay booking reference:', responseData.booking_reference);
        
        // Calculate and save payment schedule data to cart
        if (selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at && abandonedTripId) {
          try {
            const { calculatePaymentSchedule } = await import('@/utils/paymentScheduleCalculator');
            const departureDate = selectedFlight.slices[0].segments[0].departing_at;
            const paymentSchedule = calculatePaymentSchedule({
              bookingDate: new Date(),
              departureDate,
              totalWithFee: finalPriceForPayment,
              depositPercentage: selectedDeposit,
              daysBeforeDepartureForLastPayment: SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
            });
            
            // Update cart with payment schedule - AbandonedTrip.update merges flight_data
            await AbandonedTrip.update(abandonedTripId, {
              flight_data: {
                payment_schedule: {
                  deposit_percentage: selectedDeposit,
                  payment_plan: selectedPaymentPlan,
                  schedule: paymentSchedule,
                  booking_date: new Date().toISOString(),
                  departure_date: departureDate,
                  total_with_fee: finalPriceForPayment,
                  last_payment_days_before_departure: SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
                }
              }
            });
            
            console.log('✅ Payment schedule saved to cart:', {
              deposit_percentage: selectedDeposit,
              payment_plan: selectedPaymentPlan
            });
          } catch (error) {
            console.error('Failed to save payment schedule to cart:', error);
            // Non-blocking error - continue with payment flow
          }
        }
        
        // Update trip status (booking reference is derived from offer_id, no need to save it)
        await updateAbandonedTripStatus('in_cart', 'payment_initiated');
      } else {
        console.error('SlicePay response validation failed:', { status, hasEmbeddedUrl: !!responseData.embedded_flow_url, responseData });
        throw new Error(responseData.error || 'Failed to initiate payment flow');
      }
    } catch (error) {
      console.error('SlicePay initiation failed:', error);
      console.error('Error details:', error.message, error.stack);
      // Don't show error to user, just log it - they can still proceed manually
    } finally {
      setIsInitiatingPayment(false);
    }
  }, [selectedFlight, isInitiatingPayment, updateAbandonedTripStatus, appliedCredits, abandonedTripId, pricing, combinedBasePrice, selectedDeposit, selectedPaymentPlan, totalBeforeHoldFee]);

  const handleConfirmMilesBooking = useCallback(async () => {
    if (!selectedFlight || isInitiatingPayment || !abandonedTripId) return;

    // CRITICAL: Validate sufficient miles balance before booking
    const milesRequired = paymentMethod === 'miles' ? dollarsToMiles(totalWithHold) : milesUsedForPayment;
    if (milesRequired > milesBalance) {
      const shortfall = milesRequired - milesBalance;
      toast.error(`You need ${shortfall.toLocaleString()} more miles to complete this booking.`);
      return;
    }

    setIsInitiatingPayment(true);
    try {
      console.log('🎯 Confirming Miles-only booking - no SlicePay needed');
      
      // Build pricing snapshot for persistence
      const creditsTotal = appliedCredits.reduce((sum, c) => sum + c.claimedAmount, 0);
      const pricingSnapshot = {
        original_base_fare: combinedBasePrice,
        credits_total: creditsTotal,
        base_fare_after_credits: pricing.baseFareAfterCredits,
        service_fee: pricing.serviceFee,
        hold_fee: pricing.holdFee,
        seat_total: pricing.seatTotal,
        grand_total: pricing.grandTotalIncludingSeats,
        payment_method: paymentMethod,
        miles_used: pricing.milesUsed || 0,
        cash_portion: pricing.cashPortion || 0
      };
      
      // Get existing flight_data to merge
      const { data: existingCart } = await supabase
        .from('abandoned_trips')
        .select('flight_data')
        .eq('id', abandonedTripId)
        .single();
      
      const updatedFlightData = {
        ...(existingCart?.flight_data || {}),
        ...buildSeatDataForFlightData(seatSelections, selectedFlight),
        credits: appliedCredits,
        credits_total: creditsTotal,
        pricing_snapshot: pricingSnapshot
      };
      
      // Update cart status to 'ready_for_push' with pricing snapshot
      const { error: updateError } = await supabase
        .from('abandoned_trips')
        .update({ 
          status: 'ready_for_push',
          last_activity: new Date().toISOString(),
          step_reached: 'payment_confirmed',
          flight_data: updatedFlightData,
          total_amount: pricing.grandTotalIncludingSeats
        })
        .eq('id', abandonedTripId);

      if (updateError) {
        console.error('Error updating cart status:', updateError);
        throw new Error('Failed to confirm booking. Please try again.');
      }

      console.log('✅ Miles booking confirmed - cart marked as ready_for_push with pricing snapshot');
      console.log('💰 Miles will be depleted by admin when trip is pushed');
      
      // Show confirmation modal instead of toast/redirect
      setShowMilesConfirmationModal(true);
      
    } catch (error) {
      console.error('Miles booking confirmation failed:', error);
      logError({
        message: 'Miles booking confirmation failed',
        severity: 'error',
        component: 'Review.jsx',
        metadata: {
          errorMessage: error.message,
          abandonedTripId,
          hasSelectedFlight: !!selectedFlight
        }
      });
      toast.error(error.message || 'Failed to confirm booking. Please try again.');
    } finally {
      setIsInitiatingPayment(false);
    }
  }, [selectedFlight, isInitiatingPayment, abandonedTripId, navigate, seatSelections]);

  useEffect(() => {
    const initializeReviewPage = async () => {
      // A resume link is still loading its cart: the flow on screen is the
      // one this browser held before, so price, save and bounce nothing yet.
      if (resumeInFlight) return;
      // Forward navigation carries the whole selection; trust only it then.
      // Otherwise (refresh, resume link, new tab, cold start, back from sign-in
      // or a Reserve top-up) the booking flow restored from the cart is all
      // there is, and it is re-checked below before anything is priced.
      const navigatedHere = !!location.state?.selectedFlight;
      let originalOffer = navigatedHere ? location.state.selectedFlight : contextFlight;
      let returnFlight = navigatedHere ? (location.state.returnFlight ?? null) : contextReturn;
      const bookingOption = navigatedHere ? (location.state.bookingOption ?? null) : contextBookingOption;
      const passedSeatSelections = location.state?.seatSelections || [];
      const passedPassengers = location.state?.passengerDetails || contextPassengers || [];

      // Price only from the fare the customer picked, and only when it is the
      // airline's own fare for these exact flights. A cart that can't prove
      // that goes back to fare options for these flights (or a new search):
      // never priced from the search cards (audit 2026-10-27: 7% of resumed
      // carts came back at the chosen fare; the rest were priced from the
      // cards, up to 2x on round trips).
      if (originalOffer) {
        const plan = restoredCheckoutPlan({
          selectedFlight: originalOffer,
          returnFlight,
          bookingOption,
          searchParams: navigatedHere ? (location.state.searchParams ?? contextSearchParams) : contextSearchParams,
          paymentMethod: navigatedHere ? (location.state.paymentMethod ?? contextPayment) : contextPayment,
        });
        if (plan.action === 'pick_fare' || plan.action === 'search') {
          trackEvent('checkout_fare_repick', {
            reason: plan.problems?.map((p) => p.code).join(',') || 'unknown',
            to: plan.action,
            navigated: navigatedHere,
          });
          if (navigatedHere && location.state?.bookingOptionsReturn) {
            toast.info(RESTORE_PICK_FARE_MESSAGE);
            navigate(createPageUrl('FlightOptions'), { state: location.state.bookingOptionsReturn, replace: true });
          } else if (plan.action === 'pick_fare') {
            toast.info(RESTORE_PICK_FARE_MESSAGE);
            navigate(createPageUrl('FlightOptions'), { state: plan.state, replace: true });
          } else {
            toast.info(RESTORE_SEARCH_MESSAGE);
            navigate(plan.path, { replace: true });
          }
          return;
        }
      }

      console.log('🔄 Initializing Review page:', {
        hasLocationState: !!location.state,
        hasContextFlight: !!contextFlight,
        usingFlight: originalOffer ? 'found' : 'missing',
        passengersCount: passedPassengers.length
      });

      // EARLY VALIDATION: Check if data from context/state is corrupted before any processing
      // This prevents corrupted data from ever being used or persisted
      let dataIntegrityError = null;
      
      // If originalOffer already has 2 slices (from context), validate them immediately
      if (originalOffer?.slices?.length > 1) {
        const earlyValidation = validateReturnSlice(
          originalOffer.slices[0], 
          originalOffer.slices[1], 
          bookingOption || originalOffer.bookingOption
        );
        
        if (!earlyValidation.isValid && earlyValidation.isCritical) {
          console.error('🚨 CORRUPTED DATA DETECTED FROM CONTEXT:', {
            reason: earlyValidation.reason,
            correctFlightNumber: earlyValidation.correctFlightNumber
          });
          
          // LOG ERROR: Capture this validation failure for debugging
          logError({
            message: 'Flight data corrupted - validation failed on merged slices',
            severity: 'error',
            component: 'Review.jsx',
            metadata: {
              reason: earlyValidation.reason,
              correctFlightNumber: earlyValidation.correctFlightNumber,
              sliceCount: originalOffer?.slices?.length || 0,
              hasSegments: originalOffer?.slices?.map(s => s?.segments?.length || 0),
              offerId: originalOffer?.id,
              sessionId: contextSessionId
            }
          });
          
          // RECOVERY: Clear corrupted context data and redirect to search
          updateBookingState({
            selectedFlight: null,
            returnFlight: null,
            bookingOption: null
          });
          
          setOfferError('Your saved flight data was corrupted. Please search again and select your flights.');
          toast.error('Flight data was corrupted. Please search again.');
          return; // Stop processing - don't save corrupted data
        }
      }
      
      // Also validate return flight if provided separately
      if (returnFlight?.slices?.length > 0 && originalOffer?.slices?.length === 1) {
        const returnValidation = validateReturnSlice(
          originalOffer.slices[0],
          returnFlight.slices[0],
          bookingOption
        );
        
        if (!returnValidation.isValid && returnValidation.isCritical) {
          console.error('🚨 CORRUPTED RETURN FLIGHT DATA DETECTED:', {
            reason: returnValidation.reason,
            correctFlightNumber: returnValidation.correctFlightNumber
          });
          
          // LOG ERROR: Capture this validation failure for debugging
          logError({
            message: 'Return flight data was corrupted - validation failed',
            severity: 'error',
            component: 'Review.jsx',
            metadata: {
              reason: returnValidation.reason,
              correctFlightNumber: returnValidation.correctFlightNumber,
              returnSliceSegments: returnFlight?.slices?.[0]?.segments?.length || 0,
              outboundSliceSegments: originalOffer?.slices?.[0]?.segments?.length || 0,
              offerId: originalOffer?.id,
              returnOfferId: returnFlight?.id,
              sessionId: contextSessionId
            }
          });
          
          // Clear corrupted return flight from context
          updateBookingState({
            returnFlight: null
          });
          
          setOfferError('Return flight data was corrupted. Please search again and select your flights.');
          toast.error('Return flight data was corrupted. Please search again.');
          return;
        }
      }

      setSeatSelections(passedSeatSelections);
      setPassengers(passedPassengers);
      
      // Mark passengers as persisted if coming from context
      if (passedPassengers.length > 0) {
        setPassengersPersistedToDb(true);
      }

      // For Defera Flights with booking options: Use the booking option price (already includes both legs)
      // Validation already done above - proceed with merge
      
      if (originalOffer && bookingOption) {
        const bookingPrice = bookingOption.together?.price || bookingOption.departing?.price;
        
        // CRITICAL FIX: Only append returnFlight slices if originalOffer doesn't already have them
        // When booking options are used, the refreshed offer already contains both legs
        if (returnFlight && originalOffer.slices && originalOffer.slices.length === 1) {
          // Single slice = only outbound, need to append return
          // VALIDATION: Check if return slice data is valid before merging
          const returnSliceToAdd = returnFlight.slices?.[0];
          const validation = validateReturnSlice(originalOffer.slices[0], returnSliceToAdd, bookingOption);
          
          if (!validation.isValid && validation.isCritical) {
            console.error('🚫 BLOCKING CORRUPTED RETURN SLICE:', {
              reason: validation.reason,
              message: validation.message,
              correctFlightNumber: validation.correctFlightNumber
            });
            dataIntegrityError = validation.message || 'Return flight data is corrupted. Please search again and re-select your flights.';
          } else {
            originalOffer = {
              ...originalOffer,
              slices: [...(originalOffer.slices || []), ...(returnFlight.slices || [])],
              total_amount: bookingPrice.toString(),
              bookingOption: bookingOption
            };
          }
        } else if (originalOffer.slices && originalOffer.slices.length > 1) {
          // Multiple slices = already has both legs, validate integrity
          const validation = validateReturnSlice(originalOffer.slices[0], originalOffer.slices[1], bookingOption);
          
          if (!validation.isValid && validation.isCritical) {
            console.error('🚫 BLOCKING CORRUPTED EXISTING RETURN SLICE:', {
              reason: validation.reason,
              message: validation.message,
              correctFlightNumber: validation.correctFlightNumber
            });
            dataIntegrityError = validation.message || 'Return flight data is corrupted. Please search again and re-select your flights.';
          } else {
            originalOffer = {
              ...originalOffer,
              total_amount: bookingPrice.toString(),
              bookingOption: bookingOption
            };
          }
        } else {
          // No slices or single slice without return - one-way trip, no validation needed
          originalOffer = {
            ...originalOffer,
            total_amount: bookingPrice.toString(),
            bookingOption: bookingOption
          };
        }
      } else if (originalOffer && returnFlight) {
        // No booking option (a flight with no fare step). Every source quotes a
        // round trip's FULL price on both the outbound and the return offer,
        // so the return's price is the trip's price. Adding the two doubled
        // every round trip priced this way.
        const outboundPrice = parseFloat(originalOffer.total_amount || 0);
        const returnPrice = parseFloat(returnFlight.total_amount || 0);
        const totalPrice = returnPrice > 0 ? returnPrice : outboundPrice;
        
        // CRITICAL FIX: Only append if we don't already have the return slice
        const needsReturnSlice = !originalOffer.slices || originalOffer.slices.length === 1;
        
        if (needsReturnSlice && returnFlight.slices?.[0]) {
          // Validate before appending
          const validation = validateReturnSlice(originalOffer.slices?.[0], returnFlight.slices[0], null);
          if (!validation.isValid && validation.isCritical) {
            console.error('🚫 BLOCKING CORRUPTED RETURN SLICE (no bookingOption):', validation.reason);
            dataIntegrityError = validation.message || 'Return flight data is corrupted. Please search again and re-select your flights.';
          }
        }
        
        if (!dataIntegrityError) {
          originalOffer = {
            ...originalOffer,
            slices: needsReturnSlice 
              ? [...(originalOffer.slices || []), ...(returnFlight.slices || [])]
              : originalOffer.slices, // Don't append if already present
            total_amount: totalPrice.toString()
          };
        }
      }
      
      // If data integrity error detected, show error and redirect to search
      if (dataIntegrityError) {
        console.error('❌ DATA INTEGRITY ERROR - Blocking cart creation:', dataIntegrityError);
        logError({
          message: 'Data integrity error - blocking cart creation',
          severity: 'error',
          component: 'Review.jsx',
          metadata: {
            errorMessage: dataIntegrityError,
            sessionId: contextSessionId,
            hasOutboundFlight: !!originalOffer,
            hasReturnFlight: !!returnFlight,
            outboundSlices: originalOffer?.slices?.length || 0,
            returnSlices: returnFlight?.slices?.length || 0
          }
        });
        setOfferError(dataIntegrityError);
        toast.error(dataIntegrityError);
        // Don't create cart with corrupted data
        return;
      }

      if (originalOffer) {
        let currentFlightDataForTracking = originalOffer;

        const cabinClass = originalOffer.slices?.[0]?.segments?.[0]?.cabin_class || 'economy';

        if (cabinClass.toLowerCase() === 'economy') {
          setAllSegmentsCompleted(true);
        } else {
          const allSegments = originalOffer.slices?.flatMap((slice) => slice.segments) || [];
          const completedSegments = passedSeatSelections.filter((s) => s && Object.keys(s).length > 0).length;

          if (completedSegments >= allSegments.length) {
            setAllSegmentsCompleted(true);
            setCurrentSegmentIndex(0); // Reset for display
          } else {
            setAllSegmentsCompleted(false);
            setCurrentSegmentIndex(completedSegments); // Start at the next segment to select
          }
        }

        setIsRefreshingOffer(true);
        setOfferError(null);
        try {
          const offerRequestId = originalOffer.offer_request_id;
          if (!offerRequestId) {
            setSelectedFlight(originalOffer);
          } else {
            const { data: freshOfferRequestData, status } = await getOfferRequest({ offerRequestId });

            if (status !== 200 || !freshOfferRequestData.data) {
              throw new Error("Failed to retrieve latest flight data.");
            }

            const freshOffers = freshOfferRequestData.data.offers;
            let freshSelectedOffer = freshOffers.find((o) => o.id === originalOffer.id);

            if (!freshSelectedOffer) {
              console.warn("Original offer ID expired or changed. Attempting to find equivalent flight.");
              freshSelectedOffer = freshOffers.find((offer) => {
                if (!offer.slices || !originalOffer.slices || offer.slices.length !== originalOffer.slices.length) return false;
                return offer.slices.every((slice, sliceIndex) => {
                  const originalSlice = originalOffer.slices[sliceIndex];
                  return originalSlice && areSlicesEquivalent(slice, originalSlice);
                });
              });
            }

            if (freshSelectedOffer) {
              console.log("Successfully refreshed flight offer.");
              freshSelectedOffer.offer_request_id = offerRequestId; // Ensure offer_request_id is preserved
              setSelectedFlight(freshSelectedOffer);
              currentFlightDataForTracking = freshSelectedOffer; // Use refreshed data for tracking
            } else {
              throw new Error("The selected flight is no longer available. Please go back and select a new one.");
            }
          }
        } catch (error) {
          console.error("Failed to refresh flight offer:", error);
          setOfferError(error.message || "Could not verify the latest flight details. Please try again.");
          setSelectedFlight(originalOffer); // Fallback to original if refresh failed but UI needs to show something.
        } finally {
          setIsRefreshingOffer(false);
        }

        // A restored checkout re-opens the cart it came from and leaves it as
        // saved. Re-saving it here wrote a total recomputed on this page back
        // over the cart (and dropped its seats) on every refresh.
        if (navigatedHere || !(await adoptSavedCart(currentFlightDataForTracking))) {
          await trackAbandonedTrip(currentFlightDataForTracking, passedSeatSelections);
        }
      } else if (location.state || bookingFlowHydrated) {
        /** Only bounce once we KNOW there's nothing to resume. The cart rehydrates
         *  from `abandoned_trips` one network round-trip after mount, so this
         *  effect's first run always sees an empty context — redirecting there
         *  threw away a live cart and sent a customer who merely REFRESHED at
         *  checkout back to search. (Proven locally: same session, same cart —
         *  bounced on load, fully populated once the redirect was suppressed.)
         *  `location.state` short-circuits the wait for the normal forward
         *  navigation, where there is nothing to rehydrate. */
        navigate(createPageUrl('Search'));
      }
    };

    initializeReviewPage();
  }, [location.state, navigate, trackAbandonedTrip, adoptSavedCart, contextFlight, contextReturn, contextBookingOption, contextPassengers, contextSearchParams, contextPayment, bookingFlowHydrated, resumeInFlight]);

  // Derived pricing values
  const serviceFee = pricing.serviceFee;
  const holdFee = pricing.holdFee;
  const totalWithHold = pricing.grandTotalIncludingSeats;
  const milesUsedForPayment = pricing.milesUsed;
  const milesValueInDollars = pricing.milesValue;
  const cashPortion = pricing.cashPortion;
  const baseFareAfterCredits = pricing.baseFareAfterCredits || combinedBasePrice;
  
  // Net totals (credits already applied in pricing calculation, these are for display consistency)
  const netTotalAfterCredits = totalWithHold; // Credits already deducted in pricing

  /** Plan-first framing: "$X today, then N weekly payments of $Y". Same inputs
   *  as the PaymentSchedule component, so the headline and the schedule below
   *  it can never disagree. Null on Reserve / miles-only / unavailable plans. */
  const planSummary = useMemo(() => {
    try {
      const dep = selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at;
      if (!dep || paymentMethod === 'reserve' || paymentMethod === 'miles') return null;
      const scheduleArgs = {
        bookingDate: new Date(),
        departureDate: dep,
        totalWithFee: deferaZeroStyleSchedule ? pricing.subtotalBeforeHold : totalBeforeHoldFee,
        depositPercentage: deferaZeroStyleSchedule ? 0 : selectedDeposit,
        firstPaymentDays: deferaZeroStyleSchedule ? 7 : undefined,
        daysBeforeDepartureForLastPayment: lastPaymentDaysBeforeDepartureForInstallments,
        deferaCapitalDepositTiers: deferaCapitalServiceFeeTier,
        holdFeeRate: deferaCapitalServiceFeeTier ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE,
      };
      const premium = deferaZeroStyleSchedule ? 0 : insurancePremium;
      const schedule = computePlanSchedule({ ...scheduleArgs, insurancePremium: premium });
      // The cart keeps neither the insurance choice nor the Miles (a resumed
      // cart starts without both), so it keeps the travel schedule before
      // Miles: the recovery email quotes it beside the cart's travel total.
      const cartTotalWithFee = travelPricingBeforeMiles.subtotalBeforeHold ?? scheduleArgs.totalWithFee;
      const cartSchedule = premium > 0 || cartTotalWithFee !== scheduleArgs.totalWithFee
        ? computePlanSchedule({ ...scheduleArgs, totalWithFee: cartTotalWithFee, insurancePremium: 0 })
        : schedule;
      if (!schedule?.available) return null;
      const plan = selectedPaymentPlan === 'biweekly' ? schedule.biweeklyPlan : schedule.weeklyPlan;
      if (!plan?.paymentCount || !plan?.paymentAmount) return null;
      return {
        frequency: selectedPaymentPlan === 'biweekly' ? 'biweekly' : 'weekly',
        count: plan.paymentCount,
        amount: plan.paymentAmount,
        firstDate: plan.firstPaymentDate,
        lastDate: plan.finalPaymentDate,
        deposit: schedule.depositAmount,
        schedule,
        cartSchedule,
        cartTotalWithFee,
      };
    } catch {
      return null;
    }
  }, [selectedFlight, paymentMethod, deferaZeroStyleSchedule, pricing.subtotalBeforeHold, totalBeforeHoldFee, travelPricingBeforeMiles.subtotalBeforeHold, selectedDeposit, selectedPaymentPlan, lastPaymentDaysBeforeDepartureForInstallments, deferaCapitalServiceFeeTier, insurancePremium]);

  /** The cart keeps the schedule the customer is looking at — the recovery
   *  email states it verbatim. PaymentSchedule saves on selection change, but
   *  only once the cart exists; this also covers the cart id arriving AFTER the
   *  default selection fired, which left 2 of 3 review carts without a plan. */
  useEffect(() => {
    if (!abandonedTripId || !planSummary?.schedule) return;
    const dep = selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at;
    if (!dep) return;
    AbandonedTrip.update(abandonedTripId, {
      flight_data: {
        payment_schedule: {
          deposit_percentage: deferaZeroStyleSchedule ? 0 : selectedDeposit,
          ...(Number.isFinite(planSummary.cartSchedule?.depositAmount) ? { deposit_amount_cents: Math.round(planSummary.cartSchedule.depositAmount * 100) } : {}),
          payment_plan: selectedPaymentPlan,
          schedule: planSummary.cartSchedule,
          booking_date: new Date().toISOString(),
          departure_date: dep,
          total_with_fee: planSummary.cartTotalWithFee,
          last_payment_days_before_departure: lastPaymentDaysBeforeDepartureForInstallments,
        },
      },
    }).catch(() => {});
  }, [abandonedTripId, planSummary, selectedFlight, selectedDeposit, selectedPaymentPlan, deferaZeroStyleSchedule, pricing.subtotalBeforeHold, totalBeforeHoldFee, lastPaymentDaysBeforeDepartureForInstallments]);

  useEffect(() => {
    if (selectedFlight && !reviewViewedRef.current) {
      reviewViewedRef.current = true;
      trackEvent('checkout_review_viewed', { method: effectivePaymentMethod || paymentMethod || null });
    }
  }, [selectedFlight, effectivePaymentMethod, paymentMethod]);
  const netCashPortion = cashPortion; // Credits already deducted in pricing

  // Calculate miles required and check if user has sufficient balance
  const milesRequired = paymentMethod === 'miles' ? dollarsToMiles(netTotalAfterCredits) : milesUsedForPayment;
  const hasSufficientMiles = milesRequired <= milesBalance;
  const milesShortfall = milesRequired > milesBalance ? milesRequired - milesBalance : 0;
  const requiresSlicePay = !(paymentMethod === 'miles' || effectivePaymentMethod === 'defera_zero' || effectivePaymentMethod === 'defera_capital' || (paymentMethod === 'miles_installments' && cashPortion === 0));
  const paymentPrereqsReady = !!(abandonedTripId && selectedFlight && passengersPersistedToDb);

  // Auto-initiate SlicePay when all segments are completed and passengers are PERSISTED TO DB (only for search flow, not uploads)
  // SKIP SlicePay for miles-only checkout AND miles_installments when miles cover the full trip
  // CRITICAL: Wait for creditsLoaded to ensure persisted credits are applied to pricing before SlicePay
  useEffect(() => {
    // CRITICAL: Ensure cashPortion is valid (not NaN) before using it in guard
    const validCashPortion = typeof cashPortion === 'number' && !isNaN(cashPortion) ? cashPortion : null;
    
    console.log('🔍 Auto-initiate check:', { 
      fromUpload, 
      paymentMethod, 
      cashPortion: validCashPortion,
      allSegmentsCompleted, 
      passengersPersistedToDb, 
      hasSelectedFlight: !!selectedFlight, 
      hasPaymentLink: !!paymentLink, 
      isInitiatingPayment,
      hasAutoInitiated: hasAutoInitiatedPayment.current,
      creditsLoaded,
      abandonedTripId
    });

    // CRITICAL: When `installmentsCheckoutProvider` hasn't finished loading from
    // platform_settings, `effectivePaymentMethod` falls back to the raw
    // `paymentMethod` (e.g. 'installments'), which makes the
    // `effectivePaymentMethod !== 'defera_capital'` check incorrectly pass
    // and we race-fire Slice Pay before the routing decision is made. For
    // bookings that COULD route to Defera Capital or Defera Zero (anything
    // installment-flavoured), we must wait for the provider to land. Cash
    // and miles bookings can proceed without waiting — they never route
    // through the Defera tracks.
    const mightRouteToDeferaTrack =
      paymentMethod === 'installments' ||
      paymentMethod === 'miles_installments' ||
      paymentMethod === 'defera_capital';
    const safeToInitiateSlicePay =
      !mightRouteToDeferaTrack || installmentsProviderReady;

    const shouldInitiatePayment = 
      !fromUpload && // Don't auto-initiate for uploaded flights
      paymentMethod !== 'miles' && // CRITICAL: Skip SlicePay for miles-only checkout
      paymentMethod !== 'reserve' && // Skip SlicePay for Reserve checkout
      effectivePaymentMethod !== 'defera_zero' &&
      effectivePaymentMethod !== 'defera_capital' && // Skip SlicePay for Stripe installment checkout
      safeToInitiateSlicePay && // CRITICAL: Don't race the Defera Capital provider hydration
      !(paymentMethod === 'miles_installments' && validCashPortion === 0) && // CRITICAL: Skip SlicePay if miles cover entire trip
      validCashPortion !== null && // CRITICAL: Wait for valid pricing data before initiating
      creditsLoaded && // CRITICAL: Wait for credits to be hydrated from cart before initiating
      allSegmentsCompleted && 
      passengersPersistedToDb && // CRITICAL: Wait for DB save, not just React state
      travelDocsComplete && // International trips: every traveler's passport is on file
      selectedFlight && 
      abandonedTripId && // CRITICAL: Require persisted cart ID before initiating payment
      !paymentLink && 
      !isInitiatingPayment &&
      !hasAutoInitiatedPayment.current; // Only run once

    if (shouldInitiatePayment) {
      console.log('✅ Auto-initiating SlicePay - passengers confirmed persisted to database, credits loaded');
      hasAutoInitiatedPayment.current = true;
      handleInitiateSlicePay();
    } else if (paymentMethod === 'miles') {
      console.log('🎯 Miles-only payment - waiting for user to click "Confirm Booking" button');
    } else if (paymentMethod === 'miles_installments' && validCashPortion === 0) {
      console.log('🎯 Miles+Installments with full coverage - waiting for user to click "Confirm Booking" button');
    } else if (validCashPortion === null) {
      console.log('⏳ Waiting for pricing data to stabilize before auto-initiating payment');
    } else if (!creditsLoaded) {
      console.log('⏳ Waiting for credits to be loaded from cart before auto-initiating payment');
    } else if (mightRouteToDeferaTrack && !installmentsProviderReady) {
      console.log('⏳ Waiting for installments provider (Slice vs Defera Capital) to load before deciding payment route');
    }
  }, [allSegmentsCompleted, passengersPersistedToDb, travelDocsComplete, selectedFlight, paymentLink, isInitiatingPayment, fromUpload, paymentMethod, cashPortion, creditsLoaded, abandonedTripId, installmentsProviderReady, effectivePaymentMethod]);

  // Track authentication status for Travel Credits section
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setCurrentUser(session.user);
      } else {
        setCurrentUser(null);
      }
    };
    checkAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setCurrentUser(session?.user || null);
    });
    
    return () => subscription?.unsubscribe();
  }, []);

  // When promo is applied, persist pricing snapshot so server can compute Slice Pay amount
  useEffect(() => {
    if (!promoApplied || !abandonedTripId || !selectedFlight) return;
    const persistPromoPricing = async () => {
      try {
        const { data: existingCart } = await supabase
          .from('abandoned_trips')
          .select('flight_data, seat_selections')
          .eq('id', abandonedTripId)
          .single();
        if (!existingCart) return;
        const persistedSeatTotal = existingCart.seat_selections
          ? Object.values(existingCart.seat_selections).reduce((total, segmentSeats) => {
              return total + Object.values(segmentSeats || {}).reduce((segTotal, seat) => {
                const priceValue = typeof seat.price === 'object' && seat.price !== null && 'amount' in seat.price
                  ? parseFloat(seat.price.amount)
                  : parseFloat(seat.price || 0);
                return segTotal + priceValue;
              }, 0);
            }, 0)
          : seatTotalCost;
        const newPricing = calculateReviewPagePricing(
          combinedBasePrice,
          persistedSeatTotal,
          effectivePaymentMethod,
          milesBalance,
          creditsAppliedTotal,
          true, // waive service fee (promo)
          installmentsCheckoutProvider
        );
        const pricingSnapshot = {
          original_base_fare: combinedBasePrice,
          credits_total: creditsAppliedTotal,
          base_fare_after_credits: newPricing.baseFareAfterCredits,
          service_fee: 0,
          hold_fee: newPricing.holdFee,
          seat_total: persistedSeatTotal,
          grand_total: newPricing.grandTotalIncludingSeats,
          payment_method: paymentMethod,
          miles_used: newPricing.milesUsed || 0,
          cash_portion: newPricing.cashPortion ?? 0
        };
        await AbandonedTrip.update(abandonedTripId, {
          flight_data: {
            ...(existingCart.flight_data || {}),
            pricing_snapshot: pricingSnapshot
          }
        });
      } catch (e) {
        console.error('Failed to persist promo pricing snapshot:', e);
      }
    };
    persistPromoPricing();
  }, [promoApplied, abandonedTripId, selectedFlight, combinedBasePrice, seatTotalCost, effectivePaymentMethod, milesBalance, creditsAppliedTotal, installmentsCheckoutProvider]);

  // Load persisted credits from cart when abandonedTripId is set (backup for edge cases)
  // Primary loading happens in trackAbandonedTrip, this catches any missed hydration
  useEffect(() => {
    const loadPersistedCredits = async () => {
      if (!abandonedTripId) return;
      
      // If credits already loaded by trackAbandonedTrip, just ensure the flag is set
      if (creditsLoaded) {
        return;
      }
      
      try {
        // Guests read their cart through /api/guest-cart, not the table.
        const store = await getCartStore();
        const { data: cart, error } = await store.getById(abandonedTripId, 'flight_data');

        if (error) {
          console.error('Failed to load cart for credits:', error);
          setCreditsLoaded(true); // Still mark as loaded to unblock SlicePay
          return;
        }
        
        const persistedCredits = cart?.flight_data?.credits;
        if (persistedCredits && Array.isArray(persistedCredits) && persistedCredits.length > 0) {
          console.log('✅ Loaded persisted credits from cart (backup):', persistedCredits.length, 'credits');
          setAppliedCredits(persistedCredits);
        }
        if (cart?.flight_data?.promo_applied) {
          setPromoApplied(true);
        }
        setDiscount(readCartDiscount(cart?.flight_data));
        setCreditsLoaded(true); // Mark as loaded to unblock SlicePay
      } catch (error) {
        console.error('Error loading persisted credits:', error);
        setCreditsLoaded(true); // Still mark as loaded to unblock SlicePay on error
      }
    };
    
    loadPersistedCredits();
  }, [abandonedTripId, creditsLoaded]);

  // Auto-apply a code captured from a creator's ?ref= link.
  //
  // Runs once the cart exists and only when nothing is applied yet, so it can
  // never overwrite a code the customer typed themselves or a legacy fee
  // waiver. The stored value is cleared on BOTH outcomes: on success it has
  // done its job, and on rejection (expired, limit reached, wrong shape) it
  // would otherwise retry against every future cart forever.
  const refApplyAttemptedRef = useRef(false);
  useEffect(() => {
    if (!abandonedTripId || !creditsLoaded) return;
    if (discount || promoApplied) return;
    if (refApplyAttemptedRef.current) return;

    const code = readStoredReferralCode();
    if (!code) return;
    refApplyAttemptedRef.current = true;

    (async () => {
      try {
        const result = await validatePromoCode(code, abandonedTripId, { viaLink: true });
        if (result?.valid && result.kind === 'discount') {
          setDiscount({
            code: result.code,
            codeId: result.code_id ?? null,
            cents: result.discount_cents,
            viaLink: true,
          });
          toast.success(`${result.discount_label} off applied with code ${result.code}`);
        } else if (result?.valid) {
          setPromoApplied(true);
        }
      } catch {
        // Silent: the customer never asked for this, so a failure should not
        // surface as an error they have to reason about.
      } finally {
        clearStoredReferralCode();
      }
    })();
  }, [abandonedTripId, creditsLoaded, discount, promoApplied]);

  // Calculate total credits applied
  const totalCreditsApplied = useMemo(() => {
    return appliedCredits.reduce((sum, credit) => sum + credit.claimedAmount, 0);
  }, [appliedCredits]);

  // Handle credits change - update state and persist to cart with full pricing snapshot
  const handleCreditsChange = useCallback(async (newCredits) => {
    setAppliedCredits(newCredits);
    
    // Persist credits AND pricing snapshot to abandoned trip
    if (abandonedTripId && selectedFlight) {
      try {
        const { data: existingCart } = await supabase
          .from('abandoned_trips')
          .select('flight_data, seat_selections')
          .eq('id', abandonedTripId)
          .single();
        
        if (existingCart) {
          const creditsTotal = newCredits.reduce((sum, c) => sum + c.claimedAmount, 0);
          
          // Calculate seat total from persisted selections
          const persistedSeatTotal = existingCart.seat_selections 
            ? Object.values(existingCart.seat_selections).reduce((total, segmentSeats) => {
                return total + Object.values(segmentSeats || {}).reduce((segTotal, seat) => {
                  const priceValue = typeof seat.price === 'object' && seat.price !== null && 'amount' in seat.price 
                    ? parseFloat(seat.price.amount) 
                    : parseFloat(seat.price || 0);
                  return segTotal + priceValue;
                }, 0);
              }, 0)
            : seatTotalCost;
          
          // Calculate pricing with credits-first rule (waive service fee for flight+hotel or promo)
          const newPricing = calculateReviewPagePricing(
            combinedBasePrice,
            persistedSeatTotal,
            effectivePaymentMethod,
            milesBalance,
            creditsTotal,
            !!(selectedHotel && packageType === 'flight_stay') || promoApplied,
            installmentsCheckoutProvider
          );
          
          // Build complete pricing snapshot
          const pricingSnapshot = {
            original_base_fare: combinedBasePrice,
            credits_total: creditsTotal,
            base_fare_after_credits: newPricing.baseFareAfterCredits,
            service_fee: newPricing.serviceFee,
            hold_fee: newPricing.holdFee,
            seat_total: persistedSeatTotal,
            grand_total: newPricing.grandTotalIncludingSeats,
            payment_method: paymentMethod,
            miles_used: newPricing.milesUsed || 0,
            cash_portion: newPricing.cashPortion ?? 0
          };
          
          const updatedFlightData = {
            ...(existingCart.flight_data || {}),
            credits: newCredits,
            credits_total: creditsTotal,
            pricing_snapshot: pricingSnapshot
          };
          
          // Also update total_amount to reflect accurate pricing
          await AbandonedTrip.update(abandonedTripId, {
            flight_data: updatedFlightData,
            total_amount: newPricing.grandTotalIncludingSeats,
            last_activity: new Date().toISOString()
          });
          console.log('✅ Credits + pricing snapshot persisted to cart:', newCredits.length, 'credits, total:', creditsTotal, 'grand_total:', newPricing.grandTotalIncludingSeats);
        }
      } catch (error) {
        // PY2RE8 regression: previously this catch only console.error'd. Customer added
        // a $500 Delta eCredit, the cart-write threw silently, the in-memory state still
        // drove correct pricing math (charged her the right amount), but her audit trail
        // was lost and she revisited later thinking we'd dropped her credits. Surface the
        // failure so (a) we see it in logs/Sentry and (b) the customer knows to retry —
        // re-applying the credit costs them nothing and re-runs persistence.
        console.error('Failed to persist credits to cart:', error);
        toast.error(
          'We saved your credit but couldn\'t back it up to your cart. ' +
          'If you refresh this page you may need to re-add it. ' +
          'Your booking will still apply the credit correctly.',
          { duration: 8000 }
        );
      }
    }
  }, [abandonedTripId, selectedFlight, combinedBasePrice, seatTotalCost, effectivePaymentMethod, milesBalance, selectedHotel, packageType, promoApplied, installmentsCheckoutProvider]);

  const handleSeatSelection = (segmentSelections, segmentIndex) => {
    const allSegments = selectedFlight?.slices?.flatMap((slice) => slice.segments) || [];
    const newSeatSelections = [...seatSelections];

    // Ensure newSeatSelections has enough elements, filling with empty objects if necessary
    while (newSeatSelections.length <= segmentIndex) {
      newSeatSelections.push({});
    }
    newSeatSelections[segmentIndex] = segmentSelections;
    setSeatSelections(newSeatSelections);

    // Update abandoned trip with current seat selections
    if (abandonedTripId) {
      // Convert to a plain object for storage
      const seatSelectionsObject = newSeatSelections.reduce((acc, currentSegmentSeats, idx) => {
        if (currentSegmentSeats && Object.keys(currentSegmentSeats).length > 0) {
          acc[`segment_${idx}`] = currentSegmentSeats;
        }
        return acc;
      }, {});
      // flight_data carries the booking-ready seat shapes so the cart → booking
      // push inherits them (AbandonedTrip.update deep-merges flight_data).
      const seatDataForFlightData = buildSeatDataForFlightData(newSeatSelections, selectedFlight);
      AbandonedTrip.update(abandonedTripId, {
        seat_selections: seatSelectionsObject,
        ...(Object.keys(seatDataForFlightData).length > 0 && { flight_data: seatDataForFlightData }),
        last_activity: new Date().toISOString()
      }).catch((e) => console.error("Failed to update abandoned trip with seat selections:", e));
    }

    const nextSegmentIndex = segmentIndex + 1;

    if (nextSegmentIndex < allSegments.length) {
      setCurrentSegmentIndex(nextSegmentIndex);
      setAllSegmentsCompleted(false); // Reset this flag if not all segments are done
    } else {
      setAllSegmentsCompleted(true);
      setCurrentSegmentIndex(0); // Reset for display purposes
    }
  };

  const handleProceedToPassenger = async () => {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // User is not authenticated - show auth gate
      trackEvent('checkout_auth_gate_shown');
      setShowAuthGate(true);
      return;
    }
    
    // User is authenticated - proceed with passenger form
    // Several travelers on a fare whose traveler count is unknown can't be
    // fitted safely (the price may cover more or fewer): search again.
    if (travelerCountProblem(passengers.length, pricedTravelerCount) === 'unknown_multi') {
      navigate(createPageUrl('Search'));
      return;
    }
    const numPassengers = pricedTravelerCount || 1;
    if (passengers.length !== numPassengers) {
      // Keep the travelers already entered (the first ones), pad with blanks.
      setPassengers(fitTravelersToPricedCount(passengers, numPassengers, (i) => ({
        title: '', first_name: '', middle_name: '', last_name: '', date_of_birth: '', gender: '',
        passport_number: '', passport_expiry: '', nationality: '', passport_country: '', phone: '',
        email: '', frequent_flyer_number: '', known_traveler_number: '', special_requests: '',
        seat_preference: getDefaultSeatPreference(i)
      })));
    }
    // Lock body scroll when modal opens
    document.body.style.overflow = 'hidden';
    setShowPassengerForm(true);
    await updateAbandonedTripStatus('in_cart', 'passenger_details');
  };

  const handleCancelPassengerForm = () => {
    // Clear passengers if they don't have complete data. Passport details
    // don't count here: closing the form on an international trip must not
    // erase travelers entered before passports were required.
    if (!haveTravelerBasics()) {
      setPassengers([]);
    }
    // Unlock body scroll when modal closes
    document.body.style.overflow = '';
    setShowPassengerForm(false);
  };

  // Helper function to check if passengers have required data filled in.
  //
  // Stays in sync with PassengerForm's `isFormValid` (first_name, last_name,
  // date_of_birth, phone, gender). We only re-check first_name/last_name/gender
  // here because PassengerForm itself guards date_of_birth + phone — but
  // gender went unchecked for ~96% of bookings historically and led to
  // empty-gender rows that Duffel will refuse to ticket. WESX5W incident.
  /** Why the entered travelers can't be paid for (count ≠ the fare's), or null. */
  const travelerProblem = travelerCountProblem(passengers.length, pricedTravelerCount);
  /** Names and gender on every traveler, at the priced count. */
  const haveTravelerBasics = () => {
    if (passengers.length === 0) return false;
    // Every payment option is gated on this, so a traveler count that doesn't
    // match the priced fare can't reach payment on any rail.
    if (travelerProblem) return false;
    return passengers.every(passenger =>
      passenger &&
      passenger.first_name &&
      passenger.first_name.trim() !== '' &&
      passenger.last_name &&
      passenger.last_name.trim() !== '' &&
      passenger.gender &&
      passenger.gender.trim() !== ''
    );
  };
  const arePassengersComplete = () => {
    if (passengers.length === 0) return false;
    // Every payment option is gated on this, so a traveler count that doesn't
    // match the priced fare can't reach payment on any rail.
    if (travelerProblem) return false;
    // International trips also need every traveler's passport (PassengerForm
    // requires it too); until the trip's countries are known, payment waits.
    return haveTravelerBasics() && travelDocsComplete;
  };

  // Helper to normalize a date to YYYY-MM-DD format
  const normalizeDateToYYYYMMDD = (dateValue) => {
    if (!dateValue) return '';
    try {
      // Handle both ISO strings (from DB) and YYYY-MM-DD strings (from form)
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      // Format to YYYY-MM-DD in UTC to avoid timezone issues
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  // Helper to normalize phone number to digits only
  const normalizePhone = (phone) => {
    if (!phone) return '';
    return phone.toString().replace(/\D/g, '');
  };

  // Helper to normalize passenger data for comparison
  const normalizePassengerForComparison = (passenger) => {
    return {
      first_name: (passenger.first_name || passenger.firstName || passenger.givenName || '').trim().toLowerCase(),
      middle_name: (passenger.middle_name || passenger.middleName || '').trim().toLowerCase(),
      last_name: (passenger.last_name || passenger.lastName || passenger.familyName || '').trim().toLowerCase(),
      date_of_birth: normalizeDateToYYYYMMDD(passenger.date_of_birth || passenger.bornOn),
      gender: (passenger.gender || '').trim().toLowerCase(),
      email: (passenger.email || '').trim().toLowerCase(),
      phone: normalizePhone(passenger.phone || passenger.phoneNumber),
      passport_number: (passenger.passport_number || passenger.passportNumber || '').trim().toUpperCase(),
      passport_expiry: normalizeDateToYYYYMMDD(passenger.passport_expiry || passenger.passportExpiryDate),
      nationality: (passenger.nationality || '').trim().toLowerCase(),
      passport_country: (passenger.passport_country || passenger.passportCountry || '').trim().toLowerCase(),
      frequent_flyer_program: (passenger.frequent_flyer_program || passenger.frequentFlyerProgram || '').trim().toLowerCase(),
      frequent_flyer_number: (passenger.frequent_flyer_number || passenger.frequentFlyerNumber || '').trim(),
      known_traveler_number: (passenger.known_traveler_number || passenger.knownTravelerNumber || '').trim(),
      special_requests: (passenger.special_requests || passenger.specialRequests || '').trim(),
      seat_preference: normalizeSeatPreference(passenger.seat_preference || passenger.seatPreference)
    };
  };

  // Helper to compare two normalized passengers
  const arePassengersEqual = (p1, p2) => {
    const keys = ['first_name', 'middle_name', 'last_name', 'date_of_birth', 'gender', 'email', 'phone', 
                  'passport_number', 'passport_expiry', 'nationality', 'passport_country', 'frequent_flyer_program',
                  'frequent_flyer_number', 'known_traveler_number', 'special_requests',
                  'seat_preference'];
    return keys.every(key => p1[key] === p2[key]);
  };

  const handlePassengerFormComplete = async (passengerDetails, selectedSavedPassengerIds = {}) => {
    setIsProcessing(true);
    try {
      // BLACKLIST CHECK: Any matching identifier blocks booking eligibility.
      // This is checked at the moment the user attempts to save passenger info.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = { 'Content-Type': 'application/json' };
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }

        const checkResp = await fetch('/api/blacklist/check', {
          method: 'POST',
          headers,
          credentials: 'omit',
          body: JSON.stringify({ passengers: passengerDetails, abandonedTripId })
        });

        if (checkResp.ok) {
          const checkData = await checkResp.json();
          if (checkData?.matched) {
            setBlacklistBlock({ matched_types: checkData.matched_types || [] });
            setIsProcessing(false);
            return; // Do NOT save passengers
          }
        } else {
          // If blacklist check fails, do not block booking; log only.
          const t = await checkResp.text();
          console.warn('Blacklist check failed (non-blocking):', checkResp.status, t);
        }
      } catch (e) {
        console.warn('Blacklist check exception (non-blocking):', e?.message || e);
      }

      // CRITICAL: Save to database FIRST before updating React state
      // This ensures passengers are persisted before any auto-payment flow triggers
      if (abandonedTripId) {
        // Count only: traveler details include passport numbers.
        console.log('Saving passengers to database:', {
          tripId: abandonedTripId,
          passengerCount: passengerDetails.length,
        });
        
        // Batch fetch all referenced saved passengers for comparison
        const savedPassengerIds = Object.values(selectedSavedPassengerIds).filter(Boolean);
        let savedPassengersMap = {};
        
        if (savedPassengerIds.length > 0) {
          try {
            const savedPassengers = await SavedPassenger.findByUserId((await UserEntity.me()).id);
            savedPassengersMap = savedPassengers.reduce((acc, sp) => {
              acc[sp.id] = sp;
              return acc;
            }, {});
          } catch (err) {
            console.warn('Failed to fetch saved passengers for comparison:', err);
          }
        }
        
        // Auto-save each passenger as a saved passenger for future bookings
        // Only save if passenger is new or has been modified
        const passengersToSave = passengerDetails.filter((passenger, index) => {
          const selectedId = selectedSavedPassengerIds[index];
          
          // If no saved passenger was selected, this is a new passenger - save it
          if (!selectedId) {
            console.log(`Passenger ${index + 1} is new - will save`);
            return true;
          }
          
          // Check if the saved passenger still exists
          const originalSavedPassenger = savedPassengersMap[selectedId];
          if (!originalSavedPassenger) {
            console.log(`Passenger ${index + 1} references deleted saved passenger - will save as new`);
            return true;
          }
          
          // Compare normalized data
          const normalizedCurrent = normalizePassengerForComparison(passenger);
          const normalizedOriginal = normalizePassengerForComparison(originalSavedPassenger);
          
          const isModified = !arePassengersEqual(normalizedCurrent, normalizedOriginal);
          if (isModified) {
            console.log(`Passenger ${index + 1} has been modified - will save`);
          } else {
            console.log(`Passenger ${index + 1} unchanged from saved passenger - skipping save`);
          }
          
          return isModified;
        });
        
        // Use Promise.allSettled to attempt all saves even if some fail
        const saveResults = await Promise.allSettled(
          passengersToSave.map((passenger, index) => 
            SavedPassenger.upsert(passenger).catch(err => {
              console.warn(`Warning: Failed to save passenger ${index + 1}:`, err.message);
              throw err;
            })
          )
        );
        
        const successCount = saveResults.filter(r => r.status === 'fulfilled').length;
        const failCount = saveResults.filter(r => r.status === 'rejected').length;
        
        if (successCount > 0) {
          console.log(`✅ Successfully saved ${successCount}/${passengersToSave.length} new/modified passenger(s) to saved passengers list`);
        }
        if (failCount > 0) {
          console.warn(`⚠️ Failed to save ${failCount}/${passengersToSave.length} passenger(s) (non-critical)`);
        }
        if (passengerDetails.length - passengersToSave.length > 0) {
          console.log(`ℹ️ Skipped ${passengerDetails.length - passengersToSave.length} unchanged passenger(s)`);
        }
        
        const result = await AbandonedTrip.update(abandonedTripId, {
          passengers: passengerDetails,
          status: 'in_cart',
          step_reached: 'passenger_details_completed',
          last_activity: new Date().toISOString()
        });
        
        console.log('✅ Successfully saved passengers to database:', result?.id || abandonedTripId);
        
        // Persist passengers AND complete flight data to BookingFlowContext
        // Session IDs are now aligned via alignSessionId() in FlightOptions
        // This ensures context sync targets the same abandoned_trips row as the cart
        updateBookingState({
          passengers: passengerDetails,
          selectedFlight: selectedFlight  // Include merged slices to prevent data loss
        });
        
        // ONLY update React state AFTER successful database save
        setPassengers(passengerDetails);
        setPassengersPersistedToDb(true); // CRITICAL: Mark passengers as persisted - this gates the auto-SlicePay flow
      } else {
        console.error('❌ CRITICAL: No abandonedTripId - cannot save passengers');
        throw new Error('Cannot save passenger data - no trip ID available');
      }
      
      // Unlock body scroll when modal closes
      document.body.style.overflow = '';
      setShowPassengerForm(false);
      // Reveal, not just unlock: the block that appears below is where the
      // customer has to act next, so it gets put in front of them rather than
      // left half off-screen behind the nav.
      setFinalStageReveals((n) => n + 1);
      setIsProcessing(false);
    } catch (error) {
      console.error('❌ CRITICAL ERROR: Failed to save passengers:', error);
      console.error('Error details:', error.message, error.stack);
      
      // Show error to user - this is CRITICAL business data
      alert(`Failed to save passenger information: ${error.message}\n\nPlease try again. If the problem persists, contact support.`);
      
      setIsProcessing(false);
      // DO NOT close the form or update state - force user to retry
    }
  };

  /** Fires once per reveal of the final stage, never on a plain page load: a
   *  cart restored with passengers already on file must open at the top of the
   *  page like any other visit, and the counter only moves inside the submit
   *  handler above. Runs after commit, so the block is laid out and its
   *  `scroll-mt-28` (nav clearance) is already in effect. */
  useEffect(() => {
    if (!finalStageReveals) return;
    scrollFinalStageIntoView(reviewAndPayRef.current, {
      reduceMotion: prefersReducedMotion(),
    });
  }, [finalStageReveals]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // If the document becomes hidden (e.g., tab switch, app switch on mobile), consider it abandoned
      if (document.visibilityState === 'hidden' && abandonedTripId && !paymentLink) {
        updateAbandonedTripStatus('in_cart');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [abandonedTripId, paymentLink, updateAbandonedTripStatus]);

  if (isRefreshingOffer) {
    return (
      <div className="fixed inset-0 bg-black/80 z-[100] flex flex-col items-center justify-start pt-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl"
        >
          <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-color)] mx-auto mb-4" />
          <h3 className="text-lg font-light tracking-tight text-white mb-2">Finalizing Your Selection</h3>
          <p className="text-white/60 font-normal">Ensuring you have the latest prices and availability...</p>
        </motion.div>
      </div>
    );
  }

  if (offerError) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-black">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-red-500/30 max-w-md">
          <h3 className="text-lg font-light tracking-tight text-red-300 mb-2">Flight No Longer Available</h3>
          <p className="text-red-400 font-normal mb-4">{offerError}</p>
          <Button onClick={() => navigate(createPageUrl('Search'))} className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 font-normal">
            Search New Flights
          </Button>
        </motion.div>
      </div>);

  }

  if (!selectedFlight) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-black">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl">
          <h3 className="text-lg font-light tracking-tight text-white mb-2">No Flight Selected</h3>
          <p className="text-white/60 font-normal mb-4">Please select a flight to continue with your booking.</p>
          <Button onClick={() => navigate(createPageUrl('Search'))} className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 font-normal">
            Search Flights
          </Button>
        </motion.div>
      </div>);

  }

  // Show seat selection if not all segments are completed and passenger form is not shown
  if (!allSegmentsCompleted && selectedFlight && !showPassengerForm) {
    const allSegments = selectedFlight.slices?.flatMap((slice) => slice.segments) || [];
    const currentSegment = allSegments[currentSegmentIndex];

    if (!currentSegment) {
      // This case should ideally not happen if allSegments and currentSegmentIndex are managed correctly
      return (
        <div className="min-h-[70vh] flex items-center justify-center bg-black">
          <div className="text-center bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl">
            <p className="text-white font-normal mb-4">Error: Could not find flight segment for seat selection.</p>
            <Button onClick={() => navigate(createPageUrl('Search'))} className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 font-normal">
              Back to Search
            </Button>
          </div>
        </div>);

    }

    return (
      <div className="min-h-[70vh] relative">
        <RoyalAmbientBackground />
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Button onClick={() => navigate(createPageUrl('Search'))} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 font-normal mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Flight Search
            </Button>
            <h2 className="text-3xl font-light tracking-tight text-white mb-2">Select Seats - Flight Segment {currentSegmentIndex + 1} of {allSegments.length}</h2>
            <p className="text-white/85 font-normal text-sm">
              {currentSegment?.origin?.city_name || currentSegment?.origin?.name} → {currentSegment?.destination?.city_name || currentSegment?.destination?.name}
            </p>
          </motion.div>

          <SeatMap
            key={`${currentSegment.id}-${currentSegmentIndex}`}
            flightOffer={selectedFlight}
            segment={currentSegment}
            passengers={searchPassengers}
            onSeatsSelected={(selections) => handleSeatSelection(selections, currentSegmentIndex)}
            initialSelections={seatSelections[currentSegmentIndex] || {}} />

        </div>
      </div>);

  }

  // Helper function to format currency without .00 if it's a whole number
  const formatCurrency = (amount) => {
    if (amount % 1 === 0) {
      return amount.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  let upfrontAmount = null;
  
  if (paymentMethod === 'miles') {
    // Miles-only: Pay all with miles, no upfront (deduct credits from total)
    upfrontAmount = netTotalAfterCredits;
  } else if (paymentMethod === 'miles_installments') {
    // Miles+Installments: Due today = deposit% of (cash portion + service fee) — no hold fee in base
    if (totalBeforeHoldFee > 0) {
      upfrontAmount = Math.round((totalBeforeHoldFee * (selectedDeposit / 100)) * 100) / 100;
    }
  } else if (effectivePaymentMethod === 'installments' || effectivePaymentMethod === 'defera_capital') {
    // Installments & Defera Capital: deposit% of (base fare + service fee), not total with hold — same as Slice Pay
    if (totalBeforeHoldFee > 0) {
      upfrontAmount = Math.round((totalBeforeHoldFee * (selectedDeposit / 100)) * 100) / 100;
    }
  } else if (effectivePaymentMethod === 'defera_zero') {
    // Defera Zero only: $0 upfront, pay weekly or bi-weekly (Stripe subscription)
    upfrontAmount = 0;
  }

  // Defera Capital + Defera Credit: today's REAL charge is the deposit net of
  // committed credit (the backend nets it in commitDeferaCreditForCheckout and
  // vault-validates the card at $0 when fully covered). Without this, the
  // "due today" headlines show the GROSS deposit beside the credit panel's $0
  // — which reads as a payment demand (7X8Y7E, 2026-07-19). Display-only:
  // gross amounts still flow to the checkout API unchanged.
  const capitalDisplayUpfront = isDeferaCapitalCheckout && capitalCreditAllocation && upfrontAmount != null
    ? capitalCreditAllocation.net_deposit_cents / 100
    : null;
  const displayUpfrontAmount = capitalDisplayUpfront ?? upfrontAmount;


  const fmtPlanDate = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return String(iso || '');
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };
  const planLine = planSummary
    ? `then ${planSummary.count} ${planSummary.frequency === 'biweekly' ? 'bi-weekly' : 'weekly'} payment${planSummary.count === 1 ? '' : 's'} of $${formatCurrency(planSummary.amount)}`
    : null;
  const planLastLine = planSummary?.lastDate ? `last on ${fmtPlanDate(planSummary.lastDate)}` : null;
  /** Two lines under the amount due: "then 8 weekly payments of $51.56" and
   *  "last on Nov 4". A render helper (not a nested component) so React
   *  doesn't remount it every render. */
  const renderPlanLines = (stripThen = false) => (planLine ? (
    <>
      <p className="text-white/70 text-sm font-normal mb-0.5">{stripThen ? planLine.replace(/^then /, '') : planLine}</p>
      {planLastLine && <p className="text-white/50 text-sm font-normal mb-1">{planLastLine}</p>}
    </>
  ) : null);

  /** What "Email me this plan" sends — the figures on screen right now. */
  const buildPlanSnapshot = () => ({
    tripTotal: pricing.grandTotalIncludingSeats,
    ...(insurancePremium > 0 ? { insurancePremium } : {}),
    dueToday: deferaZeroStyleSchedule ? 0 : Math.ceil(displayUpfrontAmount || 0),
    plan: planSummary
      ? { frequency: planSummary.frequency, count: planSummary.count, amount: planSummary.amount, firstDate: planSummary.firstDate, lastDate: planSummary.lastDate }
      : null,
    paymentMethodLabel: deferaZeroStyleSchedule ? 'Defera Zero' : isDeferaCapitalCheckout ? 'Defera Installments' : String(paymentMethod || ''),
  });

  const capitalCreditCoversToday = capitalDisplayUpfront != null && capitalDisplayUpfront <= 0 && (upfrontAmount ?? 0) > 0;

  /** Server rejects Capital checkout without ≥$1 upfront; block UI when deposit % yields $0 due today.
   *  Deliberately GROSS (pre-credit): a credit-covered deposit is still a valid ≥$1 checkout. */
  const deferaCapitalBlockedNoUpfront =
    effectivePaymentMethod === 'defera_capital' && (upfrontAmount == null || upfrontAmount < 1);

  // GUARD: Show loading state if selectedFlight hasn't loaded yet
  if (!selectedFlight) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-white/60 font-normal">Loading flight details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MarketingTags googleAds contentsquare />
      <div className="min-h-[70dvh] relative">
      {/* Royal blue ambient wash — same treatment as the Rewards page */}
      <RoyalAmbientBackground />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 relative z-10">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(createPageUrl('Search'))}
              className="text-white/60 hover:text-white hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-white">Review & Book</h2>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>

          {/* Flight Summary */}
          <div className={REVIEW_SECTION_CARD}>
            <SectionBand
              icon={CheckCircle}
              title={fromUpload ? 'Uploaded Itinerary' : 'Selected Flights'}
              rightSlot={!fromUpload && bookingOptionsReturn ? (
                <Button
                  variant="ghost"
                  onClick={() => navigate(createPageUrl('FlightOptions'), { state: bookingOptionsReturn })}
                  className="flex-shrink-0 h-7 px-2.5 text-[11px] font-normal text-white/70 hover:text-white hover:bg-white/10 border border-white/15 rounded-full"
                >
                  <ArrowLeft className="w-3 h-3 mr-1.5" />
                  Booking Options
                </Button>
              ) : null}
            />
            <div className="px-2 md:px-4 py-4 md:py-6">
              {fromUpload && uploadedImageUrl ? (
                <div className="w-full">
                  <img 
                    src={uploadedImageUrl} 
                    alt="Uploaded Flight Itinerary" 
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              ) : (
                <div className="space-y-4 md:space-y-6">
                  {selectedFlight.slices?.map((slice, sliceIndex) => {
                    const firstSegment = slice?.segments?.[0];
                    const flightDate = firstSegment?.departing_at ? format(parseISO(firstSegment.departing_at), 'EEEE, MMMM d, yyyy') : '';
                    
                    return (
                      <div key={sliceIndex} className="px-2 md:px-0">
                        <div className="mb-3 px-2 md:px-0">
                          <h3 className="text-base md:text-lg font-light tracking-tight text-white">
                            {sliceIndex === 0 ? 'Outbound Flight' : 'Return Flight'}
                          </h3>
                          {flightDate && (
                            <p className="text-sm text-white/60 font-normal mt-1">{flightDate}</p>
                          )}
                        </div>
                        <FlightOfferCard
                          offer={{
                            ...selectedFlight,
                            slices: [slice],
                            flightData: {
                              ...(selectedFlight.flightData || selectedFlight.flight_data || {}),
                              slices: selectedFlight.slices,
                            },
                          }}
                          isReturnJourney={sliceIndex > 0}
                          hidePrice={true}
                          gradientBottomColor="#0A1233" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Hotel Summary (for Flights + Stays packages) */}
          {selectedHotel && packageType === 'flight_stay' && (
            <div className={REVIEW_SECTION_CARD}>
              <SectionBand
                icon={CheckCircle}
                title="Selected Hotel"
              />
              <div className="px-2 md:px-4 py-4 md:py-6">
                <div className="px-2 md:px-0">
                  <StayOfferCard hotel={selectedHotel} hidePrice={true} />
                  {selectedHotel.selectedRate && selectedHotel.selectedRate.room_name && (
                    <div className="mt-4 px-4 py-3 bg-brand-900/25 border border-brand-800/25 rounded-xl">
                      <div className="flex items-start gap-3">
                        <Bed className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white/90 mb-1">Room Type</div>
                          <div className="text-base text-white">{selectedHotel.selectedRate.room_name}</div>
                          {selectedHotel.selectedRate.beds && selectedHotel.selectedRate.beds.length > 0 && (
                            <div className="text-sm text-white/60 mt-1">
                              {selectedHotel.selectedRate.beds.map(bed => `${bed.count} ${bed.type} bed${bed.count > 1 ? 's' : ''}`).join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Seat Selections — shown whenever seats were picked, including free ones */}
          {hasSelectedSeats &&
            <div className={REVIEW_SECTION_CARD}>
              <SectionBand
                icon={User}
                title="Selected Seats"
              />
              <div className="px-6 py-5">
                <div className="space-y-3">
                  {seatSelections.flatMap((segmentSeats, segmentIndex) =>
                    Object.entries(segmentSeats || {}).map(([seatId, seatData]) =>
                      <div key={`${segmentIndex}-${seatId}`} className="flex justify-between items-center py-2 border-b border-white/[0.06] last:border-b-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-brand-900/35 border border-brand-800/30 rounded-lg flex items-center justify-center text-brand-400 text-sm font-normal tabular-nums">
                            {seatId}
                          </div>
                          <div>
                            <div className="text-white font-normal">
                              Seat {seatId} ({seatData.cabin?.name || 'Economy'})
                            </div>
                            <div className="text-sm text-white/60 font-normal">
                              {seatData.passenger?.first_name} {seatData.passenger?.last_name}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {parseFloat(seatData.price?.amount || seatData.price || 0) > 0 ? (
                            <>
                              <div className="text-white font-normal tabular-nums">${parseFloat(seatData.price?.amount || seatData.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              <div className="text-xs text-white/60 font-normal">per seat</div>
                            </>
                          ) : (
                            <div className="text-emerald-400/90 font-normal">Free</div>
                          )}
                        </div>
                      </div>
                    )
                  )}
                  <div className="flex justify-between items-center pt-3 font-normal">
                    <div className="text-white">Total Seat Fees</div>
                    <div className="text-white tabular-nums">
                      {seatTotalCost > 0
                        ? `$${seatTotalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : 'Free'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }

          {/* Travel Credits & Gift Cards Section - Only show after authentication */}
          <TravelCreditsSection
            appliedCredits={appliedCredits}
            onCreditsChange={handleCreditsChange}
            // Travel only, before Miles: airline credits come off the fare and
            // the Miles are sized again after them; neither pays the premium.
            maxAmount={travelPricingBeforeMiles.grandTotalIncludingSeats}
            isAuthenticated={!!currentUser}
            flightAirlineCode={selectedFlight?.slices?.[0]?.segments?.[0]?.operating_carrier?.iata_code || 
                              selectedFlight?.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code || null}
          />

          {/* Promo Code - Server-validated only, no codes in client */}
          {paymentMethod !== 'miles' && (
            <PromoCodeField
              cartId={abandonedTripId}
              applied={promoApplied}
              discount={discount}
              onApplied={(result) => {
                // The server tells us which of the two effects the code was.
                if (result?.kind === 'discount') {
                  setDiscount({
                    code: result.code,
                    codeId: result.code_id ?? null,
                    cents: result.discount_cents,
                    viaLink: false,
                  });
                } else {
                  setPromoApplied(true);
                }
              }}
              onRemoved={() => { setPromoApplied(false); setDiscount(null); }}
              disabled={!abandonedTripId}
            />
          )}

          {/* Price Calculator / Reserve Summary */}
          <div className={REVIEW_SECTION_CARD}>
            <SectionBand
              icon={CreditCard}
              title={paymentMethod === 'reserve' ? 'Reserve Summary' : 'Price Calculator'}
              subtitle={paymentMethod === 'reserve' ? 'Funds drawn from your Reserve balance' : 'Review pricing before you confirm'}
            />
            <div className="px-6 py-5">
              {/* Credits Applied Summary */}
              {creditsAppliedTotal > 0 && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-2xl px-4 py-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <span className="text-green-400 text-sm">🎫</span>
                      </div>
                      <div>
                        <div className="text-green-400 font-normal text-sm">Travel Credits Applied</div>
                        <div className="text-green-400/70 text-xs font-normal">{appliedCredits.length} credit{appliedCredits.length !== 1 ? 's' : ''} applied</div>
                      </div>
                    </div>
                    <div className="text-green-400 font-semibold text-lg">
                      -${creditsAppliedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              )}

              {/* Discount code. Quotes pricing.discountApplied rather than the
                  code's face value: if the trip shrank under an already-applied
                  code the discount is clamped, and showing "-$20.00" while
                  charging less would be a lie in the customer's favour that
                  still reads as a bug. */}
              {pricing.discountApplied > 0 && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-2xl px-4 py-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Tag className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <div className="text-green-400 font-normal text-sm">Discount applied</div>
                        <div className="text-green-400/70 text-xs font-normal">Code {pricing.discountCode}</div>
                      </div>
                    </div>
                    <div className="text-green-400 font-semibold text-lg">
                      -${pricing.discountApplied.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              )}


              {/* Defera Miles the customer applied in the payment section, as a
                  line in the price like a discount code. Quotes what the total
                  really fell by. */}
              {pricing.milesApplied > 0 && (
                <MilesAppliedLine miles={milesToUse} appliedCents={Math.round(pricing.milesApplied * 100)} />
              )}

              {/* Due at Hotel Notice - Only show for Flights+Stays packages with mandatory fees */}
              {selectedHotel && packageType === 'flight_stay' && selectedHotel.selectedRate?.due_at_accommodation_amount && parseFloat(selectedHotel.selectedRate.due_at_accommodation_amount) > 0 && (
                <div className="bg-brand-500/10 border border-brand-500/20 rounded-2xl px-4 py-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-brand-500/20 flex items-center justify-center">
                        <span className="text-brand-400 text-sm">🏨</span>
                      </div>
                      <div>
                        <div className="text-brand-400 font-normal text-sm">Due at Hotel Check-in</div>
                        <div className="text-brand-400/70 text-xs font-normal">Mandatory fee not included in payment plan</div>
                      </div>
                    </div>
                    <div className="text-brand-400 font-semibold text-lg">
                      ${parseFloat(selectedHotel.selectedRate.due_at_accommodation_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing breakdown - only show when promo code was applied successfully */}
              {paymentMethod !== 'miles' && promoApplied && (
                <div className="mb-6 space-y-2 text-sm">
                  <div className="flex justify-between text-white/60">
                    <span>Base fare</span>
                    <span className="text-white">${(pricing.baseFareAfterCredits ?? combinedBasePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-white/60">
                    <span>Service Fee {getServiceFeeDisplayLabel(pricing.serviceFee, pricing.baseFareAfterCredits ?? combinedBasePrice, deferaCapitalServiceFeeTier)}</span>
                    <span className={promoApplied ? 'text-green-400' : 'text-white'}>
                      {promoApplied ? '$0.00' : `$${pricing.serviceFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </span>
                  </div>
                  {pricing.holdFee > 0 && (
                    <div className="flex justify-between text-white/60">
                      <span>Hold fee</span>
                      <span className="text-white">${pricing.holdFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Reassurance strip — the four standing promises, right above the total
                  (open Iris recommendation 2026-08-24). Copy mirrors the knowledge base. */}
              {paymentMethod !== 'reserve' && paymentMethod !== 'miles' && (
                <CheckoutReassuranceStrip className="mb-5" />
              )}

              {/* Defera Capital: show service + hold fee breakdown (no Slice Pay receipt link) */}
              {isDeferaCapitalCheckout && (
                <Collapsible
                  defaultOpen
                  onOpenChange={(open) => trackEvent('checkout_breakdown_toggled', { open })}
                  className={cn('group mb-6', BRAND_NESTED_PANEL)}
                >
                  <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-white/70 hover:bg-brand-900/30 transition-colors font-normal">
                    <span>Price Breakdown</span>
                    {/* Trip total stays visible while collapsed — the billed number is never hidden behind the expand. */}
                    <span className="flex items-center gap-2.5">
                      <span className="text-white/90 font-medium tabular-nums">
                        ${pricing.grandTotalIncludingSeats.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-white/45 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden />
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t border-white/10 px-4 pb-4 pt-3 space-y-2.5 text-sm">
                    <div className="flex justify-between text-white/55 font-normal">
                      <span>Base fare {creditsAppliedTotal > 0 ? '(after credits)' : ''}</span>
                      <span className="text-white/90 tabular-nums">
                        ${(pricing.baseFareAfterCredits ?? combinedBasePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-white/55 font-normal">
                      <span className="flex items-center gap-1.5">
                        Service fee
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/30 hover:text-white/60 transition-colors focus:outline-none" aria-label="Service fee info">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="top"
                            align="start"
                            sideOffset={6}
                            className="w-56 rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur px-3.5 py-3 text-[12px] text-white/70 font-normal leading-relaxed shadow-xl"
                          >
                            Covers dedicated support throughout your payment plan, so you're never on your own.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-white/90 tabular-nums">
                        ${pricing.serviceFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {hasSelectedSeats && (
                      <div className="flex justify-between text-white/55 font-normal">
                        <span>Seat selection</span>
                        <span className={pricing.seatTotal > 0 ? 'text-white/90 tabular-nums' : 'text-emerald-400/90'}>
                          {pricing.seatTotal > 0
                            ? `$${pricing.seatTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : 'Free'}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-white/55 font-normal">
                      <span>Subtotal (before hold)</span>
                      <span className="text-white/90 tabular-nums">
                        ${pricing.subtotalBeforeHold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-white/55 font-normal">
                      <span className="flex items-center gap-1.5">
                        Hold fee
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/30 hover:text-white/60 transition-colors focus:outline-none" aria-label="Hold fee info">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="top"
                            align="start"
                            sideOffset={6}
                            className="w-56 rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur px-3.5 py-3 text-[12px] text-white/70 font-normal leading-relaxed shadow-xl"
                          >
                            Covers the cost of funding your full fare upfront on your behalf, so you can pay over time.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-white/90 tabular-nums">
                        ${pricing.holdFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {insurancePlan && (
                      <div className="flex justify-between text-white/55 font-normal">
                        <span>Travel insurance ({insurancePlan.label})</span>
                        <span className="text-white/90 tabular-nums">
                          ${insurancePremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-white/10 pt-2.5 mt-1 text-white font-medium">
                      <span>Trip total</span>
                      <span className="tabular-nums">
                        ${pricing.grandTotalIncludingSeats.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-[12px] text-white/55 font-normal leading-relaxed pt-1">
                      Your trip total already includes the service fee and hold fee — nothing extra is added later. The hold fee covers Defera funding your full fare upfront so you can pay over time; your deposit and installments are calculated on the amount before it.
                      {insurancePlan ? ' Travel insurance is Faye’s price with no fees on it, spread across the payments after your deposit.' : ''}
                    </p>
                    {!selectedHotel && (
                      <div className="flex justify-center pt-2">
                        <AllInPricePill />
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Miles Warning - Show for Miles/Miles+Installments when balance is low */}
              {(paymentMethod === 'miles' || paymentMethod === 'miles_installments') && !hasSufficientMiles && (
                <div className="text-center mb-6 px-4">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-2xl px-4 py-3">
                    <p className="text-red-400 font-normal text-sm mb-1">
                      ⚠️ Defera Miles
                    </p>
                    <p className="text-white/85 font-normal text-xs">
                      You need {milesShortfall.toLocaleString()} more miles to complete this booking.
                    </p>
                    <p className="text-white/40 font-normal text-xs mt-1">
                      Current balance: {milesBalance.toLocaleString()} miles • Required: {milesRequired.toLocaleString()} miles
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Schedule - Only show for installment payments (not Reserve, not miles-only).
                  Hidden, not unmounted, while Defera Miles pay for the whole trip: mounting
                  again would reset the deposit and frequency the customer chose. */}
              {paymentMethod !== 'miles' && 
               paymentMethod !== 'reserve' &&
               !(paymentMethod === 'miles_installments' && cashPortion === 0) &&
               selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at && (
                <div className={milesCoverWholeTrip ? 'hidden' : undefined}>
                <PaymentSchedule
                  bookingDate={new Date()}
                  departureDate={selectedFlight.slices[0].segments[0].departing_at}
                  totalWithFee={
                    // Defera Zero only: pass subtotalBeforeHold (11% service path). Capital uses Slice-style tiered fees like installments.
                    deferaZeroStyleSchedule
                      ? pricing.subtotalBeforeHold
                      : totalBeforeHoldFee
                  }
                  isRefundableFare={deferaZeroStyleSchedule}
                  firstPaymentDays={deferaZeroStyleSchedule ? 7 : undefined}
                  lastPaymentDaysBeforeDeparture={lastPaymentDaysBeforeDepartureForInstallments}
                  deferaCapitalDepositTiers={deferaCapitalServiceFeeTier}
                  holdFeeRate={deferaCapitalServiceFeeTier ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE}
                  insurancePremium={deferaZeroStyleSchedule ? 0 : insurancePremium}
                  onSelectionChange={async ({ deposit, plan }) => {
                    setSelectedDeposit(deposit);
                    setSelectedPaymentPlan(plan);
                    
                    // Save payment schedule to cart whenever user changes selection
                    // Use AbandonedTrip.update which merges flight_data automatically
                    if (abandonedTripId && selectedFlight?.slices?.[0]?.segments?.[0]?.departing_at) {
                      try {
                        const { calculatePaymentSchedule } = await import('@/utils/paymentScheduleCalculator');
                        const departureDate = selectedFlight.slices[0].segments[0].departing_at;
                        // Before Miles, like planSummary.cartSchedule: the cart keeps no Miles choice.
                        const totalForSchedule = travelPricingBeforeMiles.subtotalBeforeHold ?? (deferaZeroStyleSchedule
                          ? pricing.subtotalBeforeHold
                          : totalBeforeHoldFee);
                        const paymentSchedule = calculatePaymentSchedule({
                          bookingDate: new Date(),
                          departureDate,
                          totalWithFee: totalForSchedule,
                          depositPercentage: deposit,
                          firstPaymentDays: deferaZeroStyleSchedule ? 7 : undefined,
                          daysBeforeDepartureForLastPayment: lastPaymentDaysBeforeDepartureForInstallments,
                          deferaCapitalDepositTiers: deferaCapitalServiceFeeTier,
                          holdFeeRate: deferaCapitalServiceFeeTier ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE,
                          // Travel only: the cart keeps no insurance choice (see planSummary.cartSchedule).
                          insurancePremium: 0,
                        });
                        
                        // Update cart with payment schedule - AbandonedTrip.update merges flight_data.
                        // Persist `deposit_amount_cents` alongside `deposit_percentage` so any
                        // downstream consumer (Carts/EditCart admin display, abandoned-checkout
                        // recovery, accounting) can pin the exact dollar amount the customer
                        // saw — without recomputing through the calculator and risking a
                        // 1-cent drift (matters most for the new 5% / 10% DC tiers).
                        const depositAmountCentsForCart = Number.isFinite(paymentSchedule?.depositAmount)
                          ? Math.round(paymentSchedule.depositAmount * 100)
                          : null;
                        await AbandonedTrip.update(abandonedTripId, {
                          flight_data: {
                            payment_schedule: {
                              deposit_percentage: deposit,
                              ...(depositAmountCentsForCart != null ? { deposit_amount_cents: depositAmountCentsForCart } : {}),
                              payment_plan: plan,
                              schedule: paymentSchedule,
                              booking_date: new Date().toISOString(),
                              departure_date: departureDate,
                              total_with_fee: totalForSchedule,
                              last_payment_days_before_departure: lastPaymentDaysBeforeDepartureForInstallments,
                            }
                          }
                        });
                        
                        console.log('✅ Payment schedule saved to cart on selection change:', {
                          deposit_percentage: deposit,
                          payment_plan: plan
                        });
                      } catch (error) {
                        console.error('Failed to save payment schedule to cart:', error);
                        // Non-blocking error - continue
                      }
                    }
                  }}
                />
                </div>
              )}

              {/* Mini-FAQ where the hesitation happens — right under the schedule,
                  before the acknowledgments. The only copy on the page. */}
              {paymentMethod !== 'reserve' && paymentMethod !== 'miles' && (
                <CheckoutFaq variant="compact" placement="schedule" className="mb-6" insuranceOffered={Boolean(fayeOffer?.eligible) && milesToUse <= 0} />
              )}

              {/* Defera Credit (house credit) — additive preview. Only renders
                  when the customer has credit that applies to this plan. The
                  backend nets the actual charge; this just shows how it spreads. */}
              {capitalCreditAllocation && (
                <div className="mb-6 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] p-4 sm:p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
                      <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-emerald-50 tabular-nums leading-tight">
                        {effectiveCreditCents > 0
                          ? `$${fmtCurrency(effectiveCreditCents)} Defera Credit applied`
                          : 'Not using your Defera Credit on this trip'}
                      </p>
                      <p className="mt-1 text-[12px] sm:text-[13px] text-emerald-200/75 leading-snug">
                        {effectiveCreditCents > 0
                          ? "Here's how it spreads across your trip."
                          : `Your $${fmtCurrency(deferaCreditCents)} stays on your account for a future booking.`}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    {capitalCreditAllocation.deposit_credit_cents > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/70 font-normal">Toward deposit</span>
                        <span className="text-emerald-300 font-normal tabular-nums">
                          −${fmtCurrency(capitalCreditAllocation.deposit_credit_cents)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-white font-medium">Due today</span>
                      <span className="text-white font-medium tabular-nums">
                        ${fmtCurrency(capitalCreditAllocation.net_deposit_cents)}
                      </span>
                    </div>
                    {capitalCreditAllocation.installment_credit_cents > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/70 font-normal">
                          Covers {capitalCreditAllocation.installments.filter((i) => i.fully_covered).length} upcoming installment
                          {capitalCreditAllocation.installments.filter((i) => i.fully_covered).length === 1 ? '' : 's'}
                        </span>
                        <span className="text-emerald-300 font-normal tabular-nums">
                          −${fmtCurrency(capitalCreditAllocation.installment_credit_cents)}
                        </span>
                      </div>
                    )}
                    {capitalCreditAllocation.first_uncovered_installment && capitalCreditAllocation.installment_credit_cents > 0 && (
                      <p className="text-[11px] text-white/50 font-normal pt-1">
                        Your next out-of-pocket payment is installment #{capitalCreditAllocation.first_uncovered_installment.installment_number}
                        {capitalCreditAllocation.first_uncovered_installment.credit_applied_cents > 0
                          ? ` (reduced to $${fmtCurrency(capitalCreditAllocation.first_uncovered_installment.net_cents)} after credit).`
                          : '.'}
                      </p>
                    )}
                    {capitalCreditAllocation.remaining_credit_cents > 0 && (
                      <p className="text-[11px] text-emerald-200/60 font-normal">
                        ${fmtCurrency(capitalCreditAllocation.remaining_credit_cents)} in credit remains for future payments.
                      </p>
                    )}

                    {/* Choosing how much to spend. Credit applies in full by
                        default — this is the way OUT of that, not a step. */}
                    <div className="border-t border-white/10 pt-2.5 mt-1">
                      {creditEditing ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[12px] text-white/60 font-normal">Use</span>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-white/50">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={creditInput}
                              onChange={(e) => setCreditInput(e.target.value.replace(/[^0-9.]/g, ''))}
                              placeholder={fmtCurrency(deferaCreditCents)}
                              aria-label="Defera Credit to use on this trip"
                              className="w-28 h-8 rounded-lg bg-black/30 border border-white/15 pl-6 pr-2 text-[13px] text-white tabular-nums focus:outline-none focus:border-emerald-400/50"
                            />
                          </div>
                          <span className="text-[12px] text-white/45 font-normal">
                            of ${fmtCurrency(deferaCreditCents)}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const parsed = Math.round((parseFloat(creditInput) || 0) * 100);
                              setCreditSpendCents(Math.max(0, Math.min(deferaCreditCents, parsed)));
                              setCreditEditing(false);
                            }}
                            className="h-8 px-3 rounded-full bg-white text-black text-[12.5px] font-medium hover:bg-white/90 transition-colors"
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={() => { setCreditEditing(false); setCreditInput(''); }}
                            className="h-8 px-2 text-[12.5px] text-white/50 hover:text-white/80 font-normal transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 flex-wrap">
                          <button
                            type="button"
                            onClick={() => {
                              setCreditInput(effectiveCreditCents > 0 ? String((effectiveCreditCents / 100).toFixed(2)) : '');
                              setCreditEditing(true);
                            }}
                            className="text-[12px] text-emerald-200/85 hover:text-white underline underline-offset-2 decoration-white/20 hover:decoration-white/60 font-normal transition-colors"
                          >
                            Use a different amount
                          </button>
                          {creditSpendCents !== null && (
                            <button
                              type="button"
                              onClick={() => { setCreditSpendCents(null); setCreditInput(''); }}
                              className="text-[12px] text-white/45 hover:text-white/80 font-normal transition-colors"
                            >
                              Use all ${fmtCurrency(deferaCreditCents)}
                            </button>
                          )}
                          {creditSpendCents === null && (
                            <button
                              type="button"
                              onClick={() => { setCreditSpendCents(0); setCreditInput(''); }}
                              className="text-[12px] text-white/45 hover:text-white/80 font-normal transition-colors"
                            >
                              Save it for later
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Reserve Price Summary */}
              {paymentMethod === 'reserve' && (
                <div className="mb-6">
                  <div className="space-y-3">
                    {pricing.seatTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60 font-normal">Seat Selection</span>
                        <span className="text-white font-normal">${pricing.seatTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Trip Total</span>
                      <span className="text-white font-semibold">${pricing.grandTotalIncludingSeats.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60 font-normal">Reserve Balance</span>
                      <span className="text-emerald-400 font-normal">${(reserveBalanceCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {reserveShortfall > 0 && (
                      <div className="flex justify-between text-sm bg-brand-500/10 border border-brand-500/20 rounded-xl px-3 py-2">
                        <span className="text-brand-400 font-normal">Amount Due Now</span>
                        <span className="text-brand-400 font-medium">${(reserveShortfall / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {reserveShortfall === 0 && (
                      <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/25 px-3 py-2.5">
                        <div className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 ring-1 ring-inset ring-emerald-500/30">
                          <Check className="w-3 h-3 text-emerald-300" strokeWidth={2.75} />
                        </div>
                        <span className="text-sm font-medium text-emerald-50">Covered in full by your Reserve balance</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Button */}
              <div
                className="flex flex-col items-center gap-2 mt-4 relative"
                onClickCapture={(e) => {
                  const btn = e.target?.closest?.('button[type="submit"]');
                  if (btn && !btn.disabled) trackEvent('checkout_book_clicked', { method: effectivePaymentMethod || paymentMethod });
                }}
              >
                {/* Review & Pay heading + terms checkbox — only shown after
                    passenger details are submitted. Showing the heading next
                    to the "Enter Passenger Info" button looks awkward, so we
                    gate the entire block on `arePassengersComplete()`. */}
                {arePassengersComplete() && (
                  /* Scroll target for the reveal. `scroll-mt-28` (112px) is what
                     keeps the heading and the checkbox under it clear of the
                     fixed nav pill, measured against the real build at 72px on
                     mobile and 56px on desktop — without it, aiming at this
                     element parks it behind the nav. */
                  <label
                    ref={reviewAndPayRef}
                    className="block text-lg font-medium text-white w-full max-w-2xl mx-auto mb-1 scroll-mt-28"
                  >
                    Review & Pay
                  </label>
                )}

                {/* Names and birth dates as the airline will get them, in
                    words, one last look before paying (CPDUIJ booked a 6/30
                    birth date meant to be 6/23). */}
                {arePassengersComplete() && (
                  <CheckoutTravelerReview passengers={passengers} onEdit={handleProceedToPassenger} />
                )}

                {arePassengersComplete() && (
                  <CheckoutAcknowledgments
                    accepted={allAcknowledgmentsAcceptedExceptUpgrade}
                    onAcceptedChange={setAllAcknowledgments}
                    onOpenDocument={(kind) => setTermsOverlay(kind)}
                    requiredMessage={showTermsRequired ? blockedPurchaseMessage : null}
                  />
                )}

                {/* Optional flight alert texts to the lead traveler's phone
                    (server/flight-status-sms.js). Shows only once texts are on. */}
                {arePassengersComplete() && (
                  <CheckoutTextAlertsOptIn
                    phone={passengers[0]?.phone || passengers[0]?.phone_number || ''}
                    onOpenDocument={(kind) => setTermsOverlay(kind)}
                  />
                )}

                {/* ── Free Flight Upgrade ──────────────────────────────────────
                    A REQUIRED choice, not a checkbox: "No" is a legitimate answer,
                    so forcing it into the acknowledgment stack would read as one
                    more thing to consent to before buying. Two large tap targets
                    (44px+ on mobile) instead of a radio group — the whole card is
                    the hit area, which is the difference between a one-thumb
                    answer and a mis-tap at the last step of checkout.

                    Sits between the acknowledgments and the total on purpose: the
                    last thing before the price should feel like something they're
                    getting, not another disclosure. Only rendered once passenger
                    details are in, matching the acknowledgment block. */}
                {arePassengersComplete() && (
                  <div className="mb-8 text-left max-w-2xl mx-auto">
                    <div className={cn(
                      'rounded-2xl border p-4 sm:p-5 transition-colors duration-300',
                      fasterFlightAnswered
                        ? 'border-white/10 bg-white/[0.03]'
                        : 'border-brand-600/40 bg-brand-800/25',
                    )}>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600/20 ring-1 ring-inset ring-brand-600/40">
                          <PlaneTakeoff className="w-4 h-4 text-brand-300" strokeWidth={2.25} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <h3 className="text-[15px] sm:text-base font-semibold text-white leading-tight">
                              Free Flight Upgrade
                            </h3>
                            {/* Two chips, equal weight. "Upgrade" reads as a CABIN
                                upgrade to most people, so the correction has to sit
                                beside the title where a skimmer sees it — not in body
                                copy they may never reach. Someone who says yes
                                expecting business class and gets a faster economy
                                routing was mis-sold, however true the small print. */}
                            <span className="text-[10px] uppercase tracking-wider font-medium text-white/55 bg-white/[0.06] rounded-full px-2 py-0.5">
                              Same cabin
                            </span>
                            <span className="text-[10px] uppercase tracking-wider font-medium text-brand-300 bg-brand-600/15 rounded-full px-2 py-0.5">
                              No extra cost
                            </span>
                          </div>
                          {/* Concrete about what changes and what never changes —
                              a vague "we may adjust your flights" is what makes
                              people answer No. */}
                          <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">
                            If our team spots a <strong className="font-medium text-white/90">faster route</strong> to your
                            destination — a shorter total trip time or a shorter layover — may we move you to it?
                          </p>
                          <p className="mt-1.5 text-[12px] leading-relaxed text-white/45">
                            Same cabin, same fare, same travel dates &mdash; this is a faster routing, not a
                            different class of service. Departure stays close to your original time and you pay
                            nothing extra, ever. We&rsquo;ll email you if we make a change.
                          </p>
                        </div>
                      </div>

                      {/* Stacked on mobile, side-by-side from `sm`. At 375px a
                          2-up grid leaves ~80px of text width, which wrapped
                          "Yes, upgrade me" onto three lines against a one-line
                          "No thanks" — lopsided, and the smaller of the two was
                          the one we want chosen. Full-width rows also make the
                          whole option a comfortable thumb target. */}
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {[
                          { value: true, label: 'Yes, upgrade me', sub: 'Find me a faster route if one becomes available' },
                          { value: false, label: 'No thanks', sub: 'Keep these exact flights' },
                        ].map((opt) => {
                          const active = fasterFlightOptIn === opt.value;
                          return (
                            <button
                              key={String(opt.value)}
                              type="button"
                              onClick={() => setFasterFlightOptIn(opt.value)}
                              aria-pressed={active}
                              className={cn(
                                'relative text-left rounded-xl border px-3.5 py-3 min-h-[56px] sm:min-h-[62px] transition-all duration-200',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
                                active
                                  ? 'border-brand-600 bg-brand-600/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
                                  : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]',
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <span className={cn(
                                  'flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors',
                                  active ? 'border-brand-600 bg-brand-600' : 'border-white/30',
                                )}>
                                  {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                                </span>
                                <span className={cn(
                                  'text-[13px] font-medium leading-tight',
                                  active ? 'text-white' : 'text-white/80',
                                )}>
                                  {opt.label}
                                </span>
                              </div>
                              <span className="block mt-1 pl-6 text-[11px] font-normal leading-snug text-white/40">
                                {opt.sub}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Airline scope — only meaningful once they've said yes.
                          Revealed rather than always-on so the default state is a
                          single question, not three. */}
                      {fasterFlightOptIn === true && (
                        <div className="mt-4 pt-4 border-t border-white/[0.07]">
                          <p className="text-[12px] font-medium text-white/70">
                            Which airlines may we look at?
                            {fasterFlightAirlinePref === null && (
                              <span className="ml-1.5 font-normal text-white/40">Pick one to continue</span>
                            )}
                          </p>
                          <div className="mt-2.5 flex flex-wrap gap-2">
                            {[
                              { value: 'any', label: 'Any airline', sub: 'Best odds of an upgrade' },
                              { value: 'same', label: `Only ${primaryAirlineName || 'my airline'}`, sub: 'Keeps my loyalty benefits' },
                            ].map((opt) => {
                              const active = fasterFlightAirlinePref === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setFasterFlightAirlinePref(opt.value)}
                                  aria-pressed={active}
                                  className={cn(
                                    'flex-1 min-w-[46%] sm:min-w-0 text-left rounded-lg border px-3 py-2 min-h-[44px] transition-all duration-200',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
                                    active
                                      ? 'border-brand-600/70 bg-brand-600/12'
                                      : 'border-white/10 bg-white/[0.02] hover:border-white/25',
                                  )}
                                >
                                  <span className={cn('block text-[12px] font-medium leading-tight', active ? 'text-white' : 'text-white/75')}>
                                    {opt.label}
                                  </span>
                                  <span className="block mt-0.5 text-[10.5px] font-normal leading-snug text-white/40">
                                    {opt.sub}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Travel-purpose questions (2026-09-30), asked by the Defera
                    Concierge. Required, so they sit with the upgrade question
                    above the total rather than below it — a required field a
                    skimmer never scrolls to reads as a broken Continue button.
                    Like the rest of this stage, only once passenger details
                    are in: before that there's nothing to tailor yet. */}
                {arePassengersComplete() && (
                  <ConciergeTripQuestions
                    purpose={travelPurpose}
                    onPurposeChange={setTravelPurpose}
                    travelingWithOthers={travelingWithOthers}
                    onTravelingWithOthersChange={setTravelingWithOthers}
                    companionBooking={companionBooking}
                    onCompanionBookingChange={setCompanionBooking}
                    companionReference={companionBookingReference}
                    onCompanionReferenceChange={setCompanionBookingReference}
                    isMultiPassengerBooking={isMultiPassengerBooking}
                  />
                )}

                {/* Faye travel insurance — optional, after the traveler details
                    (its price depends on every traveler's age) and before the
                    total it changes. Renders nothing when switched off, off
                    Defera Capital, or when the trip isn't eligible. */}
                {arePassengersComplete() && fayeOffer?.eligible && milesToUse <= 0 && (
                  <FayeInsuranceOffer
                    offer={fayeOffer}
                    choice={insuranceChoice}
                    onChange={setInsuranceChoice}
                    paymentsCount={capitalInstallmentInfo?.count || 1}
                  />
                )}
                {/* Terms 2.17: travel insurance and Defera Miles never combine. */}
                {arePassengersComplete() && fayeOffer?.eligible && milesToUse > 0 && (
                  <p className="w-full max-w-2xl mx-auto mb-6 text-center text-white/55 text-xs font-normal" data-testid="insurance-miles-note">
                    Travel insurance isn’t available when Defera Miles are applied. Remove the Miles to add it.
                  </p>
                )}

                {/* Due today and Trip total — for Defera Stripe checkouts, show here until passengers are complete, then the Stripe form renders it inline via renderBetween */}
                {(!showDeferaStripeCheckout || !arePassengersComplete()) && (
                  <>
                    {paymentMethod === 'reserve' ? (
                    <div className="text-center mb-4 w-full">
                      <div className="text-white text-2xl font-semibold tracking-tight mb-2">
                        {reserveShortfall > 0 
                          ? `$${(reserveShortfall / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })} due now`
                          : 'Confirm Booking'
                        }
                      </div>
                      <p className="text-white/60 text-sm font-normal">
                        {reserveShortfall > 0 
                          ? `$${fmtCurrency(reserveBalanceCents)} from Reserve + $${fmtCurrency(reserveShortfall)} card payment`
                          : `$${pricing.grandTotalIncludingSeats.toLocaleString('en-US', { minimumFractionDigits: 2 })} debited from Reserve`
                        }
                      </p>
                    </div>
                  ) : deferaZeroStyleSchedule ? (
                    <div className="text-center mb-4 w-full">
                      <div className="text-white text-2xl font-semibold tracking-tight mb-1">
                        Due Today: $0
                      </div>
                      {renderPlanLines(true)}
                      <p className="text-white/60 text-sm font-normal">
                        Trip total: ${formatCurrency(pricing.grandTotalIncludingSeats)}
                      </p>
                    </div>
                  ) : upfrontAmount ? (
                    <div className="text-center mb-4 w-full">
                      <div className="text-white text-2xl font-semibold tracking-tight mb-2">
                        {paymentMethod === 'miles' 
                          ? `${dollarsToMiles(upfrontAmount).toLocaleString()} miles due today`
                          : paymentMethod === 'miles_installments' && milesUsedForPayment > 0
                          ? netCashPortion <= 0
                            ? `${milesUsedForPayment.toLocaleString()} miles due today`
                            : `${milesUsedForPayment.toLocaleString()} miles + $${formatCurrency(Math.ceil(upfrontAmount))} due today`
                          : `$${formatCurrency(Math.ceil(displayUpfrontAmount))} due today`
                        }
                      </div>
                      {paymentMethod !== 'miles' && renderPlanLines()}
                      {creditsAppliedTotal > 0 && paymentMethod !== 'miles' && (
                        <p className="text-green-400/80 text-sm font-normal mb-1">
                          After ${formatCurrency(creditsAppliedTotal)} in credits applied
                        </p>
                      )}
                      {capitalCreditAllocation?.deposit_credit_cents > 0 && (
                        <p className="text-emerald-300/80 text-sm font-normal mb-1">
                          After ${fmtCurrency(capitalCreditAllocation.deposit_credit_cents)} Defera Credit
                        </p>
                      )}
                      {capitalCreditCoversToday && (
                        <p className="text-white/65 text-xs font-normal mb-1 max-w-[19rem] mx-auto leading-snug">
                          Your card won't be charged today — it's saved securely for your installments.
                        </p>
                      )}
                      {paymentMethod === 'miles_installments' && milesUsedForPayment > 0 && netCashPortion > 0 ? (
                        <p className="text-white/60 text-sm font-normal">
                          ${formatCurrency(netCashPortion)} including taxes and fees
                        </p>
                      ) : paymentMethod !== 'miles' && paymentMethod !== 'miles_installments' ? (
                        <div>
                          <p className="text-white/85 text-sm font-normal">
                            Trip total: ${formatCurrency(pricing.grandTotalIncludingSeats)}
                          </p>
                          <p className="text-white/40 text-xs font-normal mt-0.5">
                            {selectedHotel
                              ? 'Trip total includes airline & hotel taxes and service fees.'
                              : 'Trip total includes airline taxes and service fees.'}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-center mb-4 w-full">
                      <div className="text-white text-2xl font-semibold tracking-tight mb-2">
                        {paymentMethod === 'miles'
                          ? `Total Trip: ${dollarsToMiles(netTotalAfterCredits).toLocaleString()} miles`
                          : paymentMethod === 'miles_installments' && milesUsedForPayment > 0
                          ? netCashPortion <= 0
                            ? `${milesUsedForPayment.toLocaleString()} miles`
                            : `${milesUsedForPayment.toLocaleString()} miles + $${formatCurrency(netCashPortion)}`
                          : `Total Trip: $${formatCurrency(netTotalAfterCredits)}`
                        }
                      </div>
                      {creditsAppliedTotal > 0 && paymentMethod !== 'miles' && (
                        <p className="text-green-400/80 text-sm font-normal mb-1">
                          After ${formatCurrency(creditsAppliedTotal)} in credits applied
                        </p>
                      )}
                      {paymentMethod === 'miles_installments' && milesUsedForPayment > 0 && netCashPortion > 0 ? (
                        <p className="text-white/60 text-sm font-normal">
                          ${formatCurrency(netCashPortion)} including taxes and fees
                        </p>
                      ) : paymentMethod === 'miles_installments' && netCashPortion <= 0 ? null : (
                        <p className="text-white/60 text-sm font-normal">
                          Includes taxes & service fees
                        </p>
                      )}
                    </div>
                  )}
                  {/* Transparency pill rides BELOW the trip-total/taxes lines
                      (founder placement). Hidden on flight+hotel (check-in
                      fees) and Reserve, same as the KB promise. */}
                  {paymentMethod !== 'reserve' && !selectedHotel && (
                    <div className="-mt-1 mb-4 flex justify-center">
                      <AllInPricePill />
                    </div>
                  )}
                  </>
                )}
                
                {paymentLink ? (
                  <>
                    {allAcknowledgmentsAccepted ? (
                      <a
                        href={paymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          console.log('🔗 Opening SlicePay link via anchor tag');
                          trackEvent('checkout_book_clicked', { method: 'slicepay' });
                          setHasOpenedPaymentLink(true);
                        }}
                        className={cn('inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm h-10 px-8', BRAND_PRIMARY_BTN_CLASS)}
                      >
                        <CreditCard className="w-4 h-4 mr-2 text-black" />
                        {hasOpenedPaymentLink ? 'Open Payment Link Again' : upfrontAmount ? `Book Now for $${formatCurrency(Math.ceil(upfrontAmount))}` : 'See Installment Plan'}
                      </a>
                    ) : (
                      <span
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium h-10 px-8 bg-green-400/50 text-black/60 cursor-not-allowed"
                        onClick={flagTermsRequired}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {upfrontAmount ? `Book Now for $${formatCurrency(Math.ceil(upfrontAmount))}` : 'See Installment Plan'}
                      </span>
                    )}
                    {hasOpenedPaymentLink && (
                      <p className="text-xs text-white/50">Link opened in new tab</p>
                    )}
                  </>
                
                ) : isInitiatingPayment ? (
                  <Button disabled className="bg-white/10 font-normal" size="lg">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing Payment...
                  </Button>
                ) : haveTravelerBasics() && !travelRequirement.ready && selectedFlight?.slices?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-white/60 text-sm font-normal">
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span>Loading payment options…</span>
                  </div>
                ) : !arePassengersComplete() ? (
                  <div className="flex flex-col items-center">
                    {travelerProblem && (
                      <p className="text-amber-200/90 text-sm font-normal text-center max-w-md mb-3" role="alert">
                        {travelerCountMessage(travelerProblem, passengers.length, pricedTravelerCount)}
                      </p>
                    )}
                    {!travelerProblem && passengers.length > 0 && travelRequirement.international && !travelDocsComplete && (
                      <p className="text-amber-200/90 text-sm font-normal text-center max-w-md mb-3" role="alert">
                        This trip is international, so each traveler needs passport details before payment.
                      </p>
                    )}
                    <Button
                      onClick={handleProceedToPassenger}
                      className={cn('font-normal mb-4 rounded-full', BRAND_PRIMARY_BTN_CLASS)}
                      size="lg"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {travelerProblem === 'unknown_multi'
                        ? 'Search again'
                        : travelerProblem === 'mismatch'
                          ? 'Update Travelers'
                          : passengers.length === 0 ? 'Continue to Passenger Details' : 'Add Passenger Details'}
                    </Button>
                  </div>
                ) : awaitingInstallmentsProvider ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-white/60 text-sm font-normal">
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span>Loading payment options…</span>
                  </div>
                ) : showDeferaStripeCheckout ? (
                  deferaCapitalBlockedNoUpfront && !milesCoverWholeTrip ? (
                    <div className="w-full max-w-2xl mx-auto space-y-4">
                    {/* The Miles card stays reachable: Miles that pay the whole trip need no plan. */}
                    {milesWalletRow}
                    <div className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-4 py-3 text-brand-100 text-sm font-normal">
                      Defera Capital requires at least <strong className="font-normal">$1.00</strong> due today before a
                      subscription can be created. Increase your deposit percentage above, or choose Slice Pay for this
                      booking.
                    </div>
                    </div>
                  ) : (
                  <div className="relative group w-full max-w-2xl mx-auto">
                    {(() => {
                      const slices = selectedFlight?.slices || [];
                      const firstSlice = slices[0];
                      const firstSegment = firstSlice?.segments?.[0];
                      const lastSegOfFirst = firstSlice?.segments?.slice(-1)[0];
                      const originCode = firstSegment?.origin?.iata_code;
                      const destCode = lastSegOfFirst?.destination?.iata_code;
                      const depCity = originCode ? getCityName(originCode) : (firstSegment?.origin?.city_name || 'Unknown');
                      const arrCity = destCode ? getCityName(destCode) : (lastSegOfFirst?.destination?.city_name || 'Unknown');
                      const depDate = firstSegment?.departing_at?.split('T')[0] || '';
                      const retSlice = slices[1];
                      const retDate = retSlice?.segments?.[0]?.departing_at?.split('T')[0] || null;
                      const mktAirline = firstSegment?.marketing_carrier?.name || 'Unknown';
                      const tripTypeVal = slices.length > 1 ? 'round_trip' : 'one_way';
                      const totalCents = Math.round(pricing.grandTotalIncludingSeats * 100);
                      const baseCents = Math.round(basePrice * 100);
                      const svcCents = Math.round((pricing.serviceFee || 0) * 100);
                      const holdCents = Math.round((pricing.holdFee || 0) * 100);
                      const depositCents = isDeferaCapitalCheckout && upfrontAmount != null
                        ? Math.round(upfrontAmount * 100)
                        : 0;
                      const flightDataForApi = buildFlightDataForApi({
                        selectedFlight,
                        promoApplied,
                        appliedCredits,
                        creditsAppliedTotal,
                        combinedBasePrice,
                        pricing,
                        seatData: buildSeatDataForFlightData(seatSelections, selectedFlight),
                        discount,
                        fasterFlightOptIn,
                        fasterFlightAirlinePref,
                        travelPurpose,
                        travelingWithOthers,
                        companionBooking,
                        companionBookingReference,
                        milesRedeemed: milesToUse,
                        pricedTravelerCount,
                      });
                      // Defera Miles pay for the whole trip: no card, no plan.
                      if (milesCoverWholeTrip) {
                        return (
                          <div className="space-y-4">
                            <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Payment method</p>
                            {milesWalletRow}
                            <MilesOnlyCheckout
                            flightData={flightDataForApi}
                            passengers={passengers}
                            cabinClass={resolveMarketingFareNameFromOffer(
                              flightDataForApi,
                              location.state?.searchParams?.cabin_class || 'economy',
                            )}
                            rail={effectivePaymentMethod}
                            miles={milesToUse}
                            tripTotalCents={milesTripTotalCents}
                            sessionId={contextSessionId || null}
                            disabled={!allAcknowledgmentsAccepted}
                            onMilesRefused={handleMilesRefused}
                          />
                          </div>
                        );
                      }
                      const dueTodaySummary = (
                        <>
                          {deferaZeroStyleSchedule ? (
                        <div className="text-center mb-4 w-full">
                          <div className="text-white text-2xl font-semibold tracking-tight mb-1">Due Today: $0</div>
                          {renderPlanLines(true)}
                          <p className="text-white/60 text-sm font-normal">Trip total: ${formatCurrency(pricing.grandTotalIncludingSeats)}</p>
                          {pricing.milesApplied > 0 && (
                            <p className="text-[#f3dca6]/85 text-sm font-normal mt-1">After ${fmtCurrency(Math.round(pricing.milesApplied * 100))} in Defera Miles</p>
                          )}
                        </div>
                      ) : upfrontAmount ? (
                        <div className="text-center mb-4 w-full">
                          <div className="text-white text-2xl font-semibold tracking-tight mb-2">
                            {`$${(Math.round(displayUpfrontAmount * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due today`}
                          </div>
                          {renderPlanLines()}
                          {creditsAppliedTotal > 0 && (
                            <p className="text-green-400/80 text-sm font-normal mb-1">After ${formatCurrency(creditsAppliedTotal)} in credits applied</p>
                          )}
                          {pricing.milesApplied > 0 && (
                            <p className="text-[#f3dca6]/85 text-sm font-normal mb-1">After ${fmtCurrency(Math.round(pricing.milesApplied * 100))} in Defera Miles</p>
                          )}
                          {capitalCreditAllocation?.deposit_credit_cents > 0 && (
                            <p className="text-emerald-300/80 text-sm font-normal mb-1">After ${fmtCurrency(capitalCreditAllocation.deposit_credit_cents)} Defera Credit</p>
                          )}
                          {capitalCreditCoversToday && (
                            <p className="text-white/65 text-xs font-normal mb-1 max-w-[19rem] mx-auto leading-snug">
                              Your card won't be charged today — it's saved securely for your installments.
                            </p>
                          )}
                          <div>
                            <p className="text-white/85 text-sm font-normal">
                              Trip total: ${formatCurrency(pricing.grandTotalIncludingSeats)}
                            </p>
                            <p className="text-white/40 text-xs font-normal mt-0.5">
                              {selectedHotel
                                ? 'Trip total includes airline & hotel taxes and service fees.'
                                : 'Trip total includes airline taxes and service fees.'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center mb-4 w-full">
                          <div className="text-white text-2xl font-semibold tracking-tight mb-2">
                            {`Total Trip: $${formatCurrency(netTotalAfterCredits)}`}
                          </div>
                          {creditsAppliedTotal > 0 && (
                            <p className="text-green-400/80 text-sm font-normal mb-1">After ${formatCurrency(creditsAppliedTotal)} in credits applied</p>
                          )}
                          <p className="text-white/60 text-sm font-normal">Includes taxes & service fees</p>
                        </div>
                      )}
                          {!selectedHotel && (
                            <div className="-mt-1 mb-4 flex justify-center">
                              <AllInPricePill />
                            </div>
                          )}
                        </>
                      );

                      const sharedCheckoutProps = {
                        // null = spend the whole balance, which is the default
                        // and what checkout always did. A number is the
                        // customer's cap; the server clamps it to what they
                        // actually have and can only ever spend less.
                        deferaCreditMaxCents: creditSpendCents === null ? null : effectiveCreditCents,
                        totalAmountCents: totalCents,
                        holdFeeCents: holdCents,
                        flightData: flightDataForApi,
                        baseFareCents: baseCents,
                        serviceFeeCents: svcCents,
                        departureCity: depCity,
                        arrivalCity: arrCity,
                        departureDate: depDate,
                        returnDate: retDate,
                        marketingAirline: mktAirline,
                        // I74BF5: persist the airline's actual fare brand
                        // (e.g. "Level") onto bookings.cabin_class. The
                        // search-form enum is only the fallback when the
                        // user never picked a fare in FlightOptions.
                        cabinClass: resolveMarketingFareNameFromOffer(
                          flightDataForApi,
                          location.state?.searchParams?.cabin_class || 'economy',
                        ),
                        tripType: tripTypeVal,
                        passengers,
                        disabled: !allAcknowledgmentsAccepted,
                        termsAccepted: allAcknowledgmentsAccepted,
                        paymentFrequency: selectedPaymentPlan,
                        depositCents: isDeferaCapitalCheckout ? depositCents : 0,
                        subtotalBeforeHoldCents: isDeferaCapitalCheckout ? Math.round(totalBeforeHoldFee * 100) : 0,
                        depositPercentage: isDeferaCapitalCheckout ? selectedDeposit : 0,
                        installmentCount: isDeferaCapitalCheckout ? (capitalInstallmentInfo?.count ?? 0) : 0,
                        installmentAmountCents: isDeferaCapitalCheckout ? (capitalInstallmentInfo?.amountCents ?? 0) : 0,
                        clientPaymentSchedule: isDeferaCapitalCheckout && capitalSliceSchedule?.available ? capitalSliceSchedule : null,
                        // Faye plan the customer added, re-priced by the server.
                        insurance: isDeferaCapitalCheckout && insurancePlan
                          ? buildInsuranceSelection({ plan: insurancePlan, acceptedAt: insuranceChoice.acceptedAt })
                          : null,
                        onInsuranceRefused: handleInsuranceRefused,
                        onMilesRefused: handleMilesRefused,
                        paymentMethodsTop: milesWalletRow,
                        renderBetween: dueTodaySummary,
                      };

                      // Defera Capital + NMI processor — parallel checkout track via Collect.js.
                      // Defera Zero (and any other variant) always stays on the Stripe form.
                      //
                      // CRITICAL: For Defera Capital we WAIT for the processor toggle to
                      // resolve before rendering ANY form. Previously we defaulted to the
                      // Stripe form while the hook was loading, which created a race
                      // window where a customer could click Confirm and submit through the
                      // wrong processor (see 5VOPA3 incident notes). Defera Zero is
                      // unaffected and renders immediately.
                      if (isDeferaCapitalCheckout) {
                        if (deferaCapitalProcessor === null) {
                          return <CapitalProcessorLoading />;
                        }
                        if (deferaCapitalProcessor === 'nmi') {
                          return (
                            <>
                              {/*
                                Apple Pay rides Stripe, and Capital's cards ride
                                NMI — so this screen runs two processors at once.
                                The island below renders ONLY the Apple Pay button
                                (nothing at all without a wallet card), and taking
                                it routes the booking down the Capital-on-Stripe
                                path that ran until the 2026-06-18 cutover. The
                                card form beneath stays on NMI.

                                Only one may take the booking: the island freezes
                                this form while its sheet is open, and the two
                                submit locks live on separate component instances.
                                Off unless VITE_APPLE_PAY_ENABLED=true — see
                                src/constants/applePay.js for the 5VOPA3 rationale.
                              */}
                              {applePayAvailableForRail('capital') && (
                                <DeferaZeroCheckoutForm
                                  {...sharedCheckoutProps}
                                  checkoutVariant="capital"
                                  sliceInstallmentsMode
                                  applePayOnly
                                  onApplePayActiveChange={setApplePayActive}
                                />
                              )}
                              <DeferaCapitalNmiCheckoutForm
                                {...sharedCheckoutProps}
                                tokenizationKey={import.meta.env.VITE_NMI_TOKENIZATION_KEY}
                                disabled={sharedCheckoutProps.disabled || applePayActive}
                              />
                            </>
                          );
                        }
                      }

                      return (
                        <DeferaZeroCheckoutForm
                          {...sharedCheckoutProps}
                          checkoutVariant={isDeferaCapitalCheckout ? 'capital' : 'zero'}
                          sliceInstallmentsMode={isDeferaCapitalCheckout}
                        />
                      );
                    })()}
                    {!allAcknowledgmentsAccepted && (
                      <div
                        className="absolute inset-0 cursor-not-allowed z-10"
                        onClick={flagTermsRequired}
                      />
                    )}
                  </div>
                  )
                ) : paymentMethod === 'reserve' && reserveShortfall > 0 ? (
                  <div className="w-full max-w-2xl mx-auto">
                    <div className="relative group">
                      <ReserveShortfallPaymentForm
                        reserveShortfall={reserveShortfall}
                        ensureIntentId={ensureReserveBookingIntent}
                        onPaymentComplete={(cashCents, intentId) => handleReserveDebitAndCreateBooking(cashCents || 0, intentId)}
                        onStart={() => setIsProcessingReserve(true)}
                        onError={() => setIsProcessingReserve(false)}
                        isProcessing={isProcessingReserve}
                        disabled={!allAcknowledgmentsAccepted}
                        termsAccepted={allAcknowledgmentsAccepted}
                      />
                      {!allAcknowledgmentsAccepted && !isProcessingReserve && (
                        <div
                          className="absolute inset-0 cursor-not-allowed z-10"
                          onClick={flagTermsRequired}
                        />
                      )}
                    </div>
                    {/* Secondary path: customers who'd rather top up Reserve first (instead of
                        paying the shortfall + booking in one click) used to navigate manually
                        to /app/reserve and underpay (e.g. $54 toward a $550 trip). Deep-link
                        them into Add Funds with the exact shortfall pre-filled and a return-to
                        URL so they end up back here automatically. */}
                    <div className="mt-3 text-center text-xs text-white/40 font-normal">
                      Prefer to top up Reserve separately first?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          const slices = selectedFlight?.slices || [];
                          const firstSeg = slices[0]?.segments?.[0];
                          const lastSeg = slices[0]?.segments?.slice(-1)[0];
                          const route = firstSeg
                            ? `${firstSeg.origin?.iata_code || '???'} → ${lastSeg?.destination?.iata_code || '???'} flight`
                            : 'flight';
                          const params = new URLSearchParams({
                            topup_for_cents: String(reserveShortfall),
                            topup_for_label: route,
                            return_to: '/Review',
                          });
                          navigate(`/app/reserve?${params.toString()}`);
                        }}
                        className="text-white/70 underline hover:text-white transition-colors"
                      >
                        Top up ${fmtCurrency(reserveShortfall)}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative group">
                    <Button
                      onClick={
                        paymentMethod === 'reserve'
                          ? handleReserveBooking
                          : paymentMethod === 'miles' 
                          ? handleConfirmMilesBooking 
                          : paymentMethod === 'miles_installments' && cashPortion === 0
                          ? handleConfirmMilesBooking
                          : handleInitiateSlicePay
                      }
                      className={cn('font-semibold py-7 rounded-full disabled:opacity-50 disabled:cursor-not-allowed', BRAND_PRIMARY_BTN_CLASS)}
                      size="lg"
                      disabled={
                        !allAcknowledgmentsAccepted ||
                        isProcessing ||
                        isProcessingReserve ||
                        (paymentMethod !== 'reserve' && requiresSlicePay && !paymentPrereqsReady) ||
                        ((paymentMethod === 'miles' || (paymentMethod === 'miles_installments' && cashPortion === 0)) && !hasSufficientMiles)
                      }
                    >
                      {(isProcessing || isProcessingReserve) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      {isProcessingReserve ? 'Processing...' : isProcessing ? 'Processing...' : paymentMethod === 'reserve' ? 'Confirm Booking' : (paymentMethod === 'miles' || (paymentMethod === 'miles_installments' && cashPortion === 0)) ? 'Confirm Booking' : 'Continue to Payment'}
                    </Button>
                    {/* Click overlay when terms not accepted - shows "Must accept terms" */}
                    {!allAcknowledgmentsAccepted && !isProcessing && (
                      <div
                        className="absolute inset-0 cursor-not-allowed z-10"
                        onClick={flagTermsRequired}
                      />
                    )}
                    {/* Tooltip for disabled state */}
                    {!hasSufficientMiles && (paymentMethod === 'miles' || (paymentMethod === 'miles_installments' && cashPortion === 0)) && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        Need {milesShortfall.toLocaleString()} more miles to book
                      </div>
                    )}
                    {requiresSlicePay && !paymentPrereqsReady && (
                      <div className="text-center text-xs text-white/60 mt-2">
                        Preparing your payment link… please stay on this page.
                      </div>
                    )}
                  </div>
                )}
                {arePassengersComplete() && paymentMethod !== 'reserve' && !(paymentMethod === 'miles' || (paymentMethod === 'miles_installments' && cashPortion === 0)) && (
                  <div className="flex flex-col items-center mt-3 mb-4">
                    {!showDeferaStripeCheckout && (
                      <>
                        <img src="/extended_image.png" alt="Accepted payment cards" className="h-6 object-contain" />
                        <p className="text-white/40 text-[10px] font-normal mt-2">{CHECKOUT_TRUST_LINE}</p>
                      </>
                    )}
                    <CheckoutTalkToTeam className={showDeferaStripeCheckout ? 'mt-0' : 'mt-5'} />
                  </div>
                )}
                {/* Always visible once a cart exists — the hesitation happens
                    before passenger details are entered, too. Social proof by
                    the CTA, and the loss-prevention capture right under it. */}
                {paymentMethod !== 'reserve' && paymentMethod !== 'miles' && (
                  <div className="flex flex-col items-center gap-2 mt-3">
                    <a
                      href="https://www.trustpilot.com/review/defera.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[12px] font-normal text-white/60 hover:text-white/85 transition-colors"
                    >
                      <Star className="h-3.5 w-3.5 text-emerald-400 fill-emerald-400" aria-hidden />
                      Excellent · 4.5 out of 5 on Trustpilot
                    </a>
                    <EmailMyPlan
                      cartId={abandonedTripId}
                      sessionId={contextSessionId}
                      knownEmail={currentUser?.email || null}
                      buildSnapshot={buildPlanSnapshot}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Defera Reserve "Add to Reserve" entry point removed —
              Reserve was discontinued for new users; existing customers
              manage their plans from /app/reserve. */}

          {/* The mobile promo video that used to sit here was a distraction on a
              payment page, not reassurance. The mini-FAQ lives under the payment
              schedule — one copy, where the decision is made. */}
        </motion.div>
      </div>

      {/* Passenger Form Modal */}
      {showPassengerForm &&
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-black/40 backdrop-blur-md md:backdrop-blur-xl overflow-y-auto" onClick={(e) => {
          if (e.target === e.currentTarget) {
            // Don't allow closing by clicking outside
          }
        }}>
          <div className={cn(REVIEW_SECTION_CARD, 'py-6 w-full max-w-2xl relative mt-24 mb-8')}>
            <div className="px-4">
              <PassengerForm
                selectedFlight={selectedFlight}
                seatSelections={seatSelections}
                passengers={passengers}
                onComplete={handlePassengerFormComplete}
                onCancel={handleCancelPassengerForm}
                travelRequirement={travelRequirement} />
            </div>
          </div>
        </div>
      }

      {/* Blacklist Block Overlay */}
      {blacklistBlock && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-black/95 border border-red-500/30 rounded-3xl max-w-lg w-full p-8"
          >
            <div className="text-center">
              <div className="text-red-300 text-lg font-light mb-2">Booking Unavailable</div>
              {/* Formal register on purpose, but it must never name a REASON.
                *  A restriction can be set for any number of reasons, including
                *  simply not wanting to continue the relationship — so the old
                *  copy ("contingent upon ... the absence of any outstanding or
                *  past-due payment obligations") told non-payment cases
                *  something untrue about themselves. A customer who is fully
                *  paid up could disprove it in one screenshot, which turns a
                *  quiet decline into an argument.
                *
                *  "Sole discretion" mirrors the right we actually reserve in
                *  Terms (src/pages/Terms.jsx — "decline, suspend, restrict,
                *  cancel, reverse, or refuse any booking"), so the statement is
                *  backed by the agreement rather than asserting a fact about
                *  the customer.
                *
                *  Substance must stay in lockstep with what the concierge is
                *  REQUIRED to say (server/tommy-concierge.js — no reason, no
                *  "blacklist"/"blocked"/"fraud", offer a human review), because
                *  the Contact Support button below routes straight into that
                *  chat. Register may differ; the story may not. */}
              <p className="text-white/85 font-normal text-sm leading-relaxed">
                We regret to inform you that we are unable to process your reservation at this time. Booking eligibility is determined at Defera&rsquo;s sole discretion, and we are unable to provide further detail regarding this determination. If you believe this determination was made in error, please contact our support team for further assistance.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <Button
                  onClick={() => {
                    setBlacklistBlock(null);
                    setIsProcessing(false);
                  }}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/10"
                >
                  OK
                </Button>
                <Button
                  onClick={() => {
                    navigate(buildTommyUrl({ message: TOMMY_INTENTS.bookingBlocked }));
                  }}
                  className="w-full bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Terms of Service / Privacy Policy full-screen overlay */}
      {termsOverlay && (
        <div className="fixed inset-0 z-[120] flex flex-col bg-black">
          <iframe
            title={termsOverlay === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            src={termsOverlay === 'terms' ? '/terms?embed=1' : '/privacy?embed=1'}
            className="flex-1 w-full min-h-0 border-0 relative z-0"
          />
          {/* Invisible top layer: pointer-events-none so iframe stays clickable; close button has pointer-events-auto so it receives clicks */}
          <div className="absolute inset-0 z-10 pointer-events-none">
            <button
              type="button"
              onClick={() => setTermsOverlay(null)}
              className="absolute top-3 right-3 p-2 rounded-full text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 pointer-events-auto"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Miles Confirmation Modal */}
      {showMilesConfirmationModal && (
        <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-black/95 backdrop-blur-xl border border-white/20 rounded-3xl max-w-lg w-full p-8 shadow-2xl"
          >
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-400/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
              </div>
              
              <h2 className="text-2xl font-light text-white mb-4">
                Trip Has Been Booked
              </h2>
              
              <p className="text-white/85 font-normal text-base mb-6 leading-relaxed">
                Please allow 2-5 hours for your trip to be processed and confirmed.
                You will see your trip populate in your Trips tab once confirmed.
              </p>
              
              <Button
                onClick={() => {
                  setShowMilesConfirmationModal(false);
                  navigate(createPageUrl('Home'));
                }}
                className="w-full bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white font-normal rounded-full h-12"
                size="lg"
              >
                Got It
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Auth Gate Modal */}
      <AuthGate
        isOpen={showAuthGate}
        onClose={() => setShowAuthGate(false)}
      />
    </div>
    </>
  );
}
